import { IsometricMap, BiomeType, clearTileCache } from './IsometricMap';
import { Player } from '@client/entities/Player';
import {
  Enemy,
  Kormos,
  AlKormos,
  Karabasan,
  Albasti,
  ErlikGuard,
  Karakura,
  Sulbus,
  ErlikOkcusu,
  AlbastiKralicesi,
  // Act 2 enemies
  DemirKurt,
  DagDevi,
  RuzgarRuhu,
  AtesIblisi,
  KayaGolemi,
  YerSub,
  // Act 3 enemies
  GolgeAvcisi,
  KemikSavasci,
  LanetliRuh,
  OlumHabercisi,
  ErlikElcisi,
  // Act 4 enemies
  ErlikSovalyesi,
  KaranlikSeraf,
  ErlikHan,
  setEnemyFrameTime,
} from '@client/entities/Enemy';
import { Renderer } from '@client/core/Renderer';
import { Camera } from '@client/core/Camera';
import { CombatSystem, CombatContext } from '@server/game/combat/CombatSystem';
import { DroppedItem } from '@client/entities/DroppedItem';
import { SkillEffect, ProjectileEffect, createSkillEffect } from '@client/effects/SkillEffect';
import { BeamEffect, createBeamEffect } from '@client/effects/BeamEffect';
import { AuraEffect, createAuraEffect } from '@client/effects/AuraEffect';
import { CustomSkill, SkillCreator, HotbarSkill, isSkillV2 } from '@shared/skills/SkillCreator';
import { CustomSkillV2, SkillCreatorV2 } from '@shared/skills/SkillCreatorV2';
import { TrapEntity, createTrapEntity } from '@client/entities/TrapEntity';
import { TotemEntity, createTotemEntity } from '@client/entities/TotemEntity';
import { MinionEntity, createMinionEntity } from '@client/entities/MinionEntity';
import { Minimap } from '@client/ui/Minimap';
import { Vector2 } from '@shared/types';
import { BossAttackResult } from '@client/entities/BossAI';
import { getParticleSystem, ParticleSystem } from '@client/effects/ParticleSystem';
import { getScreenShake, ScreenShake } from '@client/effects/ScreenShake';
import { ZoneManager, ZoneConfig, EnemySpawnType, ZoneType } from '@shared/world/Zone';
import { NPC } from '@client/entities/NPC';
import { getZoneNPCs } from '@client/entities/NPCDefinitions';
import {
  getTriggerEffectManager,
  resolveEffectAction,
  TriggerEventData,
} from '@shared/modifiers';
import { Waypoint } from '@client/effects/Waypoint';
import { getWaypointManager } from '@shared/world/WaypointManager';
import { getAudioManager } from '@client/audio/AudioManager';
import { createQuestItem, getItemColor } from '@shared/items/Item';
import { rollEffectStoneDrop } from '@shared/items/EffectStoneGenerator';
import { LegendaryItem, LegendaryEffect } from '@shared/items/LegendaryItems';
import { debugLog, debugWarn } from '@shared/utils/Logger';
import { COMBAT } from '@shared/constants';
import { STATUS_DURATIONS } from '@shared/effects/StatusEffect';
import { t } from '@locales/index';
import { getDamageNumbers, DamageNumberType } from '@client/systems/DamageNumbers';
import { LootFilter } from '@client/systems/LootFilter';
import { WeatherSystem } from '@client/systems/WeatherSystem';
import { getEnemySpriteRegistry } from '@client/core/EnemySpriteRegistry';

// Module-level skill type classification sets (avoids per-cast array allocation)
const ELEMENT_CHAIN_COLORS: Record<string, string> = {
  physical: '#cccccc',
  od: '#ff8844',
  su: '#44aaff',
  gok: '#ffff44',
  karanlik: '#bb66ff',
};
const MELEE_SKILL_TYPES = new Set(['slash', 'cleave', 'thrust', 'slam', 'stab', 'backstab', 'bash', 'block_counter', 'charge', 'melee']);
const PROJECTILE_SKILL_TYPES = new Set(['projectile', 'arrow', 'multishot']);
const AOE_SKILL_TYPES = new Set(['aoe', 'explosion', 'shockwave', 'spin', 'totem', 'rain']);
const INSTANT_SKILL_TYPES = new Set(['beam', 'chain']);

/**
 * Extract on-hit effects (slow, knockback, dot) from a skill's socketed stones.
 */
function getStoneOnHitEffects(skill: HotbarSkill): {
  slowPercent: number;
  knockbackForce: number;
  dot: { damage: number; duration: number } | null;
} {
  let slowPercent = 0;
  let knockbackForce = 0;
  let dot: { damage: number; duration: number } | null = null;
  for (const stone of skill.sockets) {
    if (!stone) continue;
    if (stone.effect.slow) slowPercent = Math.min(COMBAT.MAX_SLOW_PERCENT_FROM_STONES, slowPercent + stone.effect.slow);
    if (stone.effect.knockback) knockbackForce = Math.max(knockbackForce, stone.effect.knockback);
    if (stone.effect.dot && (!dot || stone.effect.dot.damage > dot.damage)) {
      dot = { ...stone.effect.dot };
    }
  }
  return { slowPercent, knockbackForce, dot };
}

export class GameWorld {
  public map: IsometricMap;
  public player: Player;
  public enemies: Enemy[] = [];
  public droppedItems: DroppedItem[] = [];
  public skillEffects: SkillEffect[] = [];
  public camera: Camera;
  public minimap: Minimap;

  // V2 Skill Entities
  public traps: TrapEntity[] = [];
  public totems: TotemEntity[] = [];
  public minions: MinionEntity[] = [];
  public beamEffects: BeamEffect[] = [];
  public auraEffects: AuraEffect[] = [];

  // Effects systems
  private particleSystem: ParticleSystem;
  private screenShake: ScreenShake;
  private weatherSystem: WeatherSystem;

  // Zone system
  private zoneManager: ZoneManager;
  private currentZoneConfig: ZoneConfig;

  // NPCs (for safe zones)
  public npcs: NPC[] = [];
  private highlightedNPC: NPC | null = null;

  // Waypoint (for zones with waypoints)
  private waypoint: Waypoint | null = null;

  private maxEnemies: number = 15;

  // Pre-allocated render list to avoid per-frame allocation
  private renderEntities: Array<{ entity: { position: { y: number }; render: (r: Renderer, c: Camera) => void }; y: number }> = [];

  // Pre-allocated visible torch list (reused per frame)
  private _visibleTorches: Array<{ x: number; y: number }> = [];

  // Progress tracking for saves
  public totalKills: number = 0;
  public bossesDefeated: number = 0;

  // Boss encounter zoom tracking
  private wasBossActive: boolean = false;

  // Boss cinematic state
  private bossCinematic: {
    active: boolean;
    timer: number;
    duration: number;
    bossName: string;
    bossTitle: string;           // subtitle / epithet shown beneath the name
    letterboxProgress: number;   // 0-1 for bar height
    nameAlpha: number;           // 0-1 for name text opacity
    titleAlpha: number;          // 0-1 for title text opacity
    vignetteAlpha: number;       // 0-1 for dark vignette overlay
    shockwaveProgress: number;   // 0-1 expanding ring on reveal
    phase: 'letterbox_in' | 'name_show' | 'letterbox_out' | 'done';
    /** Set of boss IDs already encountered this session - prevents re-trigger */
    encounteredBosses: Set<string>;
  } = {
    active: false,
    timer: 0,
    duration: 3.8,
    bossName: '',
    bossTitle: '',
    letterboxProgress: 0,
    nameAlpha: 0,
    titleAlpha: 0,
    vignetteAlpha: 0,
    shockwaveProgress: 0,
    phase: 'done',
    encounteredBosses: new Set(),
  };

  // Cached boss cinematic gradients (avoid creating per frame)
  private _bossVigGrad: CanvasGradient | null = null;
  private _bossGlowGrad: CanvasGradient | null = null;
  private _bossTopBarGrad: CanvasGradient | null = null;
  private _bossBotBarGrad: CanvasGradient | null = null;
  private _bossCineW: number = 0;
  private _bossCineH: number = 0;
  private _bossCineBarH: number = 0;

  // Legendary effect cooldown tracking
  private legendaryEffectCooldowns: Map<string, number> = new Map();

  // Tracked timeouts for cleanup on zone transitions
  private pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

  // Quest callbacks
  private onEnemyKilled: ((enemyType: string, isBoss: boolean) => void) | null = null;
  private onZoneEntered: ((zoneId: string) => void) | null = null;
  private onItemCollected: ((itemId: string) => void) | null = null;
  private isQuestActive: ((questId: string) => boolean) | null = null;

  // Cached torch world positions (recomputed on zone change)
  private cachedTorchWorldPositions: Array<{ x: number; y: number }> = [];

  // Shared frame time for render path (avoids Date.now() calls)
  private _frameTime: number = 0;

  // Throttle ambient particle emission (every 100ms instead of every frame)
  private _ambientTimer: number = 0;

  // Cached LootFilter singleton reference (avoids repeated getInstance() calls)
  private _lootFilter: LootFilter | null = null;

  // Thrust dash animation state
  private _thrustDash: {
    startX: number; startY: number;
    endX: number; endY: number;
    duration: number; elapsed: number;
  } | null = null;

  // Cached active boss reference (avoids iterating all enemies every frame)
  private _activeBoss: Enemy | null = null;

  /** Returns true if a boss fight is currently in progress */
  isBossActive(): boolean {
    return this._activeBoss !== null && this._activeBoss.isAlive && !this._activeBoss.isDying;
  }

  /** Returns boss info for HUD display */
  getBossInfo(): { active: boolean; name?: string; hp?: number; maxHp?: number } {
    if (this._activeBoss && this._activeBoss.isAlive && !this._activeBoss.isDying) {
      return {
        active: true,
        name: this._activeBoss.name,
        hp: this._activeBoss.stats.hp,
        maxHp: this._activeBoss.stats.maxHp,
      };
    }
    return { active: false };
  }

  // Boss phase announcement state
  private phaseAnnounce: {
    active: boolean;
    text: string;
    timer: number;
    duration: number;
  } = { active: false, text: '', timer: 0, duration: 2.0 };

  // Zone name animation state
  private zoneNameAnim: {
    active: boolean;
    zoneName: string;
    levelMin: number;
    levelMax: number;
    timer: number;       // total time elapsed
    duration: number;    // total animation duration
  } = { active: false, zoneName: '', levelMin: 0, levelMax: 0, timer: 0, duration: 3.5 };

  constructor(screenWidth: number, screenHeight: number, className: import('@shared/types').ClassName = 'alp') {
    // Initialize zone system
    this.zoneManager = new ZoneManager('otag_koyu');
    this.currentZoneConfig = this.zoneManager.getCurrentZone();
    this.applyZoneSettings();

    // Create map with zone-specific dimensions and template
    const biome = this.getZoneBiome(this.currentZoneConfig.id);
    const dimensions = this.getZoneDimensions(this.currentZoneConfig.id);
    this.map = new IsometricMap(dimensions.width, dimensions.height, biome);
    this.map.setZone(this.currentZoneConfig.id, biome);

    // Create camera
    this.camera = new Camera(screenWidth, screenHeight);

    // Create player at spawn point or map center
    const playerSpawn = this.map.getPlayerSpawn() || this.map.getCenter();
    this.player = new Player({
      x: playerSpawn.x,
      y: playerSpawn.y,
      className: className,
    });
    this.player.setMap(this.map);

    // Camera follows player
    this.camera.setTarget(this.player.position);

    // Rebuild cached torch positions for initial zone
    this.rebuildTorchCache();

    // Initialize minimap
    this.minimap = new Minimap(this.player);
    this.minimap.setMap(this.map);
    this.minimap.setScreenSize(screenWidth, screenHeight);

    // Initialize effects systems
    this.particleSystem = getParticleSystem();
    this.screenShake = getScreenShake();
    this._lootFilter = LootFilter.getInstance();
    this.weatherSystem = new WeatherSystem();
    this.weatherSystem.setZone(this.currentZoneConfig.id);
    getAudioManager().playZoneMusic(this.currentZoneConfig.id);

    // Spawn initial enemies (if not safe zone)
    if (!this.currentZoneConfig.isSafeZone) {
      this.spawnInitialEnemies();
    }
  }

  private applyZoneSettings(): void {
    // Apply zone-specific settings
    this.maxEnemies = this.currentZoneConfig.maxEnemies;

    // Spawn NPCs for safe zones
    this.spawnZoneNPCs();

    // Spawn waypoint if zone has one
    this.spawnZoneWaypoint();
  }

  // Zone-specific map dimensions (width x height)
  private getZoneDimensions(zoneId: ZoneType): { width: number; height: number } {
    const zoneDimensions: Partial<Record<ZoneType, { width: number; height: number }>> = {
      // Act 1
      'otag_koyu': { width: 65, height: 55 },        // Village - circular hub
      'karanlik_orman': { width: 75, height: 60 },   // Dark forest - winding paths
      'ruh_batakligi': { width: 70, height: 55 },    // Swamp - island archipelago
      'erlik_tapinagi': { width: 55, height: 70 },   // Temple - tall dungeon
      // Act 2
      'ergenekon_girisi': { width: 100, height: 90 },   // Mountain pass
      'firtina_ovasi': { width: 120, height: 100 },     // Storm steppe - wide open
      'ates_magarasi': { width: 90, height: 85 },       // Fire cave with lava
      'yersub_tahti': { width: 80, height: 100 },       // Underground palace - tall
      // Act 3
      'karanlik_tunel': { width: 65, height: 35 },   // Tunnel - very wide, short
      'olum_nehri': { width: 50, height: 60 },
      'kemik_ormani': { width: 55, height: 55 },
      'lanetli_saray': { width: 50, height: 55 },
      // Act 4
      'erlik_kapisi': { width: 45, height: 50 },
      'cehennem_bahcesi': { width: 55, height: 50 },
      'taht_odasi': { width: 50, height: 55 },       // Boss room - medium
    };
    return zoneDimensions[zoneId] || { width: 50, height: 50 };
  }

  private getZoneBiome(zoneId: ZoneType): BiomeType {
    // Map zones to biomes based on their theme
    const zoneBiomeMap: Partial<Record<ZoneType, BiomeType>> = {
      // Act 1 - Forest/Swamp themes
      'otag_koyu': 'sacred',
      'karanlik_orman': 'forest',
      'ruh_batakligi': 'swamp',
      'erlik_tapinagi': 'underworld',
      // Act 2 - Ergenekon (Mountain/Steppe/Volcanic themes)
      'ergenekon_girisi': 'mountains',
      'firtina_ovasi': 'desert',
      'ates_magarasi': 'desert',
      'yersub_tahti': 'underworld',
      // Act 3 - Yeraltı (Underworld themes)
      'karanlik_tunel': 'underworld',
      'olum_nehri': 'underworld',
      'kemik_ormani': 'underworld',
      'lanetli_saray': 'hell',
      // Act 4 - Erlik Diyarı (Hell themes)
      'erlik_kapisi': 'hell',
      'cehennem_bahcesi': 'hell',
      'taht_odasi': 'hell',
    };

    return zoneBiomeMap[zoneId] || 'forest';
  }

  private spawnZoneNPCs(): void {
    this.npcs = [];

    const npcList = getZoneNPCs(this.currentZoneConfig.id);
    for (const npcConfig of npcList) {
      this.npcs.push(new NPC(npcConfig));
    }
    if (this.npcs.length > 0) {
      debugLog('GameWorld', `Spawned ${this.npcs.length} NPCs in ${this.currentZoneConfig.name}`);
    }
  }

  private spawnZoneWaypoint(): void {
    this.waypoint = null;

    // Spawn waypoint if zone has one defined
    if (this.currentZoneConfig.waypointPosition) {
      const zoneId = this.currentZoneConfig.id as ZoneType;
      this.waypoint = new Waypoint({
        x: this.currentZoneConfig.waypointPosition.x,
        y: this.currentZoneConfig.waypointPosition.y,
        name: this.currentZoneConfig.name,
        activated: getWaypointManager().isWaypointActivated(zoneId),
      });
    }
  }

  getWaypoint(): Waypoint | null {
    return this.waypoint;
  }

  getNPCInRange(): NPC | null {
    for (const npc of this.npcs) {
      if (npc.isInRange(this.player.position.x, this.player.position.y)) {
        return npc;
      }
    }
    return null;
  }

  updateNPCHighlights(): void {
    // Single pass: find nearby NPC and update highlights in one loop
    let nearbyNPC: NPC | null = null;
    for (const npc of this.npcs) {
      if (!nearbyNPC && npc.isInRange(this.player.position.x, this.player.position.y)) {
        nearbyNPC = npc;
        npc.setHighlighted(true);
      } else {
        npc.setHighlighted(false);
      }
    }
    this.highlightedNPC = nearbyNPC;
  }

  getHighlightedNPC(): NPC | null {
    return this.highlightedNPC;
  }

  getCurrentZone(): ZoneConfig {
    return this.currentZoneConfig;
  }

  getCurrentZoneId(): string {
    return this.currentZoneConfig.id;
  }

  getZoneName(): string {
    return this.currentZoneConfig.name;
  }

  // Quest callback setters
  setOnEnemyKilled(callback: (enemyType: string, isBoss: boolean) => void): void {
    this.onEnemyKilled = callback;
  }

  setOnZoneEntered(callback: (zoneId: string) => void): void {
    this.onZoneEntered = callback;
  }

  setOnItemCollected(callback: (itemId: string) => void): void {
    this.onItemCollected = callback;
  }

  setIsQuestActive(callback: (questId: string) => boolean): void {
    this.isQuestActive = callback;
  }

  setQuestObjectiveLocations(_locations: Array<{ x: number; y: number; label?: string }>): void {
    // TODO: feed to HTML minimap
  }

  changeZone(zoneId: ZoneType, skipEnemySpawn: boolean = false, force: boolean = false): boolean {
    if (!force) {
      const check = this.zoneManager.canEnterZone(zoneId, this.player.level);

      if (!check.canEnter) {
        debugLog('GameWorld',` Cannot enter ${zoneId}: ${check.reason}`);
        return false;
      }
    }

    // Clear current enemies and items
    this.enemies = [];
    this.droppedItems = [];
    this._activeBoss = null;
    // Clear Karakura pack registry
    Karakura.activeKarakuras = [];
    // Clear V2 skill entities
    this.traps = [];
    this.totems = [];
    this.beamEffects = [];
    this.auraEffects = [];
    // Minions persist across zones (they follow the player), but reset their targets
    for (const minion of this.minions) {
      if (minion.isAlive) {
        minion.position.x = this.player.position.x + (Math.random() - 0.5) * 60;
        minion.position.y = this.player.position.y + (Math.random() - 0.5) * 60;
      }
    }
    this.player.stopAllChanneling();
    // Clear pending timeouts from previous zone (e.g. boss death effects)
    for (const tid of this.pendingTimeouts) clearTimeout(tid);
    this.pendingTimeouts.length = 0;
    // Purge expired legendary cooldowns (keep only entries < 60s old)
    const now = Date.now() / 1000;
    for (const [key, ts] of this.legendaryEffectCooldowns) {
      if (now - ts > 60) this.legendaryEffectCooldowns.delete(key);
    }

    // --- Memory cleanup before loading new zone ---
    // Clear old map's cached rendering data (terrain buffer, vignette, sprite batches)
    this.map.clearZoneCache();
    // Clear tile sprite and procedural terrain caches
    clearTileCache();
    // Flush particles and floating texts from the old zone
    this.particleSystem.clear();
    // Deactivate all pooled damage numbers from previous zone
    getDamageNumbers().clearAll();

    // Change zone
    this.currentZoneConfig = this.zoneManager.changeZone(zoneId);
    this.applyZoneSettings();

    // Regenerate map with new zone template and dimensions
    const biome = this.getZoneBiome(zoneId);
    const dimensions = this.getZoneDimensions(zoneId);
    this.map = new IsometricMap(dimensions.width, dimensions.height, biome);
    this.map.setZone(zoneId, biome);
    this.player.setMap(this.map);
    this.minimap.setMap(this.map);

    // Rebuild cached torch positions for new zone
    this.rebuildTorchCache();

    // Update weather for new zone
    this.weatherSystem.setZone(zoneId);

    // Play zone-appropriate music
    getAudioManager().playZoneMusic(zoneId);

    // Trigger zone name animation
    this.zoneNameAnim = {
      active: true,
      zoneName: this.currentZoneConfig.name,
      levelMin: this.currentZoneConfig.levelRange.min,
      levelMax: this.currentZoneConfig.levelRange.max,
      timer: 0,
      duration: 3.5,
    };

    // Move player to spawn point or center (unless returning via portal)
    if (!skipEnemySpawn) {
      const spawnPoint = this.map.getPlayerSpawn() || this.map.getCenter();
      this.player.position.x = spawnPoint.x;
      this.player.position.y = spawnPoint.y;
    }

    // Spawn enemies if not safe zone and not skipping
    if (!this.currentZoneConfig.isSafeZone && !skipEnemySpawn) {
      this.spawnInitialEnemies();
    }

    // Lazy-load enemy sprites for this zone (async, procedural fallback until ready)
    const spriteRegistry = getEnemySpriteRegistry();
    spriteRegistry.loadForZone(zoneId);
    spriteRegistry.unloadExcept([zoneId]);

    // Trigger zone entered callback for quests
    if (this.onZoneEntered) {
      this.onZoneEntered(zoneId);
    }

    debugLog('GameWorld',` Entered zone: ${this.currentZoneConfig.name}${skipEnemySpawn ? ' (restored)' : ''}`);
    return true;
  }

  // Restore zone state from stored data (used when returning via portal)
  restoreZoneState(state: {
    zoneId: string;
    playerPosition: { x: number; y: number };
    enemies: Array<{ type: string; x: number; y: number; hp: number; isAlive: boolean }>;
    droppedItems: Array<{ x: number; y: number; item: any }>;
  }): void {
    // Don't regenerate map - just restore enemies and items

    // Restore enemies that were alive
    this.enemies = [];
    for (const enemyData of state.enemies) {
      if (enemyData.isAlive) {
        const enemy = this.createEnemyByTypeName(enemyData.type, enemyData.x, enemyData.y);
        if (enemy) {
          enemy.stats.hp = enemyData.hp;
          enemy.setTarget(this.player);
          enemy.setMap(this.map);
          this.enemies.push(enemy);
        }
      }
    }

    // Restore dropped items
    this.droppedItems = [];
    for (const itemData of state.droppedItems) {
      const droppedItem = new DroppedItem(itemData.x, itemData.y, itemData.item);
      this.droppedItems.push(droppedItem);
    }

    debugLog('GameWorld',` Restored zone state: ${state.enemies.filter(e => e.isAlive).length} enemies, ${state.droppedItems.length} items`);
  }

  // Enemy factory registry: maps class name to [factory, typeId]
  private static readonly ENEMY_REGISTRY: Record<string, { create: (x: number, y: number) => Enemy; typeId: string }> = {
    'Kormos': { create: (x, y) => new Kormos(x, y), typeId: 'kormos' },
    'AlKormos': { create: (x, y) => new AlKormos(x, y), typeId: 'al_kormos' },
    'Karabasan': { create: (x, y) => new Karabasan(x, y), typeId: 'karabasan' },
    'Albasti': { create: (x, y) => new Albasti(x, y), typeId: 'albasti' },
    'Karakura': { create: (x, y) => new Karakura(x, y), typeId: 'karakura' },
    'Sulbus': { create: (x, y) => new Sulbus(x, y), typeId: 'sulbus' },
    'ErlikOkcusu': { create: (x, y) => new ErlikOkcusu(x, y), typeId: 'erlik_okcusu' },
    'ErlikGuard': { create: (x, y) => new ErlikGuard(x, y), typeId: 'erlik_guard' },
    'AlbastiKralicesi': { create: (x, y) => new AlbastiKralicesi(x, y), typeId: 'albasti_kralicesi' },
    'DemirKurt': { create: (x, y) => new DemirKurt(x, y), typeId: 'demir_kurt' },
    'DagDevi': { create: (x, y) => new DagDevi(x, y), typeId: 'dag_devi' },
    'RuzgarRuhu': { create: (x, y) => new RuzgarRuhu(x, y), typeId: 'ruzgar_ruhu' },
    'AtesIblisi': { create: (x, y) => new AtesIblisi(x, y), typeId: 'ates_iblisi' },
    'KayaGolemi': { create: (x, y) => new KayaGolemi(x, y), typeId: 'kaya_golemi' },
    'YerSub': { create: (x, y) => new YerSub(x, y), typeId: 'yer_sub' },
    'GolgeAvcisi': { create: (x, y) => new GolgeAvcisi(x, y), typeId: 'golge_avcisi' },
    'KemikSavasci': { create: (x, y) => new KemikSavasci(x, y), typeId: 'kemik_savasci' },
    'LanetliRuh': { create: (x, y) => new LanetliRuh(x, y), typeId: 'lanetli_ruh' },
    'OlumHabercisi': { create: (x, y) => new OlumHabercisi(x, y), typeId: 'olum_habercisi' },
    'ErlikElcisi': { create: (x, y) => new ErlikElcisi(x, y), typeId: 'erlik_elcisi' },
    'ErlikSovalyesi': { create: (x, y) => new ErlikSovalyesi(x, y), typeId: 'erlik_sovalyesi' },
    'KaranlikSeraf': { create: (x, y) => new KaranlikSeraf(x, y), typeId: 'karanlik_seraf' },
    'ErlikHan': { create: (x, y) => new ErlikHan(x, y), typeId: 'erlik_han' },
  };

  // Create enemy by class name string (used for save/load)
  private createEnemyByTypeName(typeName: string, x: number, y: number): Enemy | null {
    const entry = GameWorld.ENEMY_REGISTRY[typeName];
    if (entry) {
      const enemy = entry.create(x, y);
      enemy.enemyTypeId = entry.typeId;
      enemy.spawnPosition = { x, y };
      this.initEnemyAIByType(enemy, entry.typeId);
      return enemy;
    }
    debugWarn('GameWorld', `Unknown enemy type: ${typeName}`);
    return null;
  }

  getZoneExits(): { position: { x: number; y: number }; targetZone: string }[] {
    return this.zoneManager.getExits();
  }

  setScreenSize(width: number, height: number): void {
    this.camera.setScreenSize(width, height);
  }

  toggleMinimap(): void {
    this.minimap.toggle();
  }

  isMinimapExpanded(): boolean {
    return this.minimap.isExpandedMode();
  }

  closeMinimap(): void {
    if (this.minimap.isExpandedMode()) this.minimap.toggle();
  }

  private spawnInitialEnemies(): void {
    // Get defined spawn points from map
    const enemySpawns = this.map.getEnemySpawns();

    if (enemySpawns.length > 0) {
      // Spawn enemies at ALL defined spawn points
      for (const spawn of enemySpawns) {
        const enemy = this.createRandomEnemy(spawn.x, spawn.y);
        enemy.setTarget(this.player);
        enemy.setMap(this.map);
        this.enemies.push(enemy);
      }
    } else {
      // Fallback: spawn maxEnemies around the map
      const mapCenter = this.map.getCenter();
      for (let i = 0; i < this.maxEnemies; i++) {
        const angle = Math.random() * Math.PI * 2;
        const distance = 200 + Math.random() * 400;
        const x = mapCenter.x + Math.cos(angle) * distance;
        const y = mapCenter.y + Math.sin(angle) * distance;

        const enemy = this.createRandomEnemy(x, y);
        enemy.setTarget(this.player);
        enemy.setMap(this.map);
        this.enemies.push(enemy);
      }
    }

    // Spawn boss at designated boss spawn point if zone has a bossType
    if (this.currentZoneConfig.bossType) {
      const bossSpawn = this.map.getBossSpawn();
      if (bossSpawn) {
        const boss = this.createEnemyByType(this.currentZoneConfig.bossType, bossSpawn.x, bossSpawn.y);
        // Ensure boss rarity for HUD boss bar and cinematic trigger
        if (boss.rarity !== 'boss') {
          boss.rarity = 'boss';
          // Scale up stats for boss encounter
          boss.stats.maxHp *= 5;
          boss.stats.hp = boss.stats.maxHp;
          boss.attackDamage *= 2;
        }
        boss.setTarget(this.player);
        boss.setMap(this.map);
        this.enemies.push(boss);
        this._activeBoss = boss;
        debugLog('GameWorld',` Spawned boss ${this.currentZoneConfig.bossType} at (${bossSpawn.x.toFixed(0)}, ${bossSpawn.y.toFixed(0)})`);
      }
    }
  }

  private createRandomEnemy(x: number, y: number): Enemy {
    // Use zone's spawn configuration
    const enemyType = this.zoneManager.selectEnemyType(this.player.level);

    return this.createEnemyByType(enemyType || 'kormos', x, y);
  }

  private createEnemyByType(type: EnemySpawnType, x: number, y: number): Enemy {
    // Handle special spawn side-effects
    if (type === 'karakura') this.spawnWolfPack(x, y);
    if (type === 'demir_kurt') this.spawnIronWolfPack(x, y);
    if (type === 'kemik_savasci') this.spawnBoneWarriorPair(x, y);

    // Find matching registry entry by typeId
    let enemy: Enemy | null = null;
    for (const [, entry] of Object.entries(GameWorld.ENEMY_REGISTRY)) {
      if (entry.typeId === type) {
        enemy = entry.create(x, y);
        enemy.enemyTypeId = entry.typeId;
        enemy.zoneId = this.currentZoneConfig?.id || '';
        break;
      }
    }

    // Default fallback
    if (!enemy) {
      enemy = new Kormos(x, y);
      enemy.enemyTypeId = 'kormos';
      enemy.zoneId = this.currentZoneConfig?.id || '';
    }

    // Initialize AI behavior based on enemy type
    enemy.spawnPosition = { x, y };
    this.initEnemyAIByType(enemy, type);

    // Scale enemy stats to zone level for progressive difficulty
    if (this.currentZoneConfig) {
      const { min, max } = this.currentZoneConfig.levelRange;
      const zoneLevel = min + Math.floor(Math.random() * (max - min + 1));
      enemy.scaleToLevel(zoneLevel);
    }

    return enemy;
  }

  /** Assign appropriate AI behavior based on enemy type */
  private initEnemyAIByType(enemy: Enemy, type: string): void {
    switch (type) {
      // --- Melee aggressors: rush and attack ---
      case 'kormos':
      case 'al_kormos':
      case 'albasti':
      case 'erlik_guard':
      case 'kemik_savasci':
      case 'erlik_sovalyesi':
        enemy.initEnemyAI('melee_aggro');
        break;

      // --- Ranged kiters: keep distance, shoot projectiles ---
      case 'karabasan':
        enemy.initEnemyAI('ranged_kite', { preferredRange: 150 });
        break;
      case 'erlik_okcusu':
        enemy.initEnemyAI('ranged_kite', { preferredRange: 180 });
        break;
      case 'ruzgar_ruhu':
        enemy.initEnemyAI('ranged_kite', { preferredRange: 160 });
        break;
      case 'karanlik_seraf':
        enemy.initEnemyAI('ranged_kite', { preferredRange: 170 });
        break;

      // --- Caster/AOE: medium range, reposition periodically ---
      case 'sulbus':
        enemy.initEnemyAI('caster_aoe', { preferredRange: 100, alertDelay: 0 });
        break;
      case 'lanetli_ruh':
        enemy.initEnemyAI('caster_aoe', { preferredRange: 120 });
        break;
      case 'ates_iblisi':
        enemy.initEnemyAI('caster_aoe', { preferredRange: 130 });
        break;
      case 'olum_habercisi':
        enemy.initEnemyAI('caster_aoe', { preferredRange: 140 });
        break;

      // --- Swarm flankers: surround the player ---
      case 'karakura':
      case 'demir_kurt':
        enemy.initEnemyAI('swarm_flank');
        break;

      // --- Bosses: use BossAI system ---
      case 'albasti_kralicesi':
        enemy.initBossAI('albasti_kralicesi');
        break;
      case 'yer_sub':
        enemy.initBossAI('yer_sub');
        break;
      case 'erlik_elcisi':
        enemy.initBossAI('erlik_elcisi');
        break;
      case 'erlik_han':
        enemy.initBossAI('erlik_han');
        break;

      // --- Tanky melee with slow patrol ---
      case 'dag_devi':
      case 'kaya_golemi':
        enemy.initEnemyAI('melee_aggro', { patrolRadius: 60, leashRange: 400 });
        break;
      case 'golge_avcisi':
        enemy.initEnemyAI('melee_aggro', { alertDelay: 0.3, leashRange: 500 });
        break;

      default:
        enemy.initEnemyAI('melee_aggro');
        break;
    }
  }

  private spawnIronWolfPack(x: number, y: number): void {
    // Spawn 1 additional iron wolf
    const angle = Math.random() * Math.PI * 2;
    const distance = 40 + Math.random() * 30;

    const wolfX = x + Math.cos(angle) * distance;
    const wolfY = y + Math.sin(angle) * distance;

    const wolf = new DemirKurt(wolfX, wolfY);
    wolf.enemyTypeId = 'demir_kurt';
    wolf.spawnPosition = { x: wolfX, y: wolfY };
    wolf.initEnemyAI('swarm_flank');
    wolf.setTarget(this.player);
    wolf.setMap(this.map);
    this.enemies.push(wolf);
  }

  private spawnWolfPack(x: number, y: number): void {
    // Spawn 1-2 additional wolves near the first one
    const packSize = 1 + Math.floor(Math.random() * 2);

    for (let i = 0; i < packSize; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = 30 + Math.random() * 40;

      const wolfX = x + Math.cos(angle) * distance;
      const wolfY = y + Math.sin(angle) * distance;

      const wolf = new Karakura(wolfX, wolfY);
      wolf.enemyTypeId = 'karakura';
      wolf.spawnPosition = { x: wolfX, y: wolfY };
      wolf.initEnemyAI('swarm_flank');
      wolf.setTarget(this.player);
      wolf.setMap(this.map);
      this.enemies.push(wolf);
    }
  }

  private spawnBoneWarriorPair(x: number, y: number): void {
    // 50% chance to spawn a second bone warrior
    if (Math.random() < 0.5) {
      const angle = Math.random() * Math.PI * 2;
      const distance = 35 + Math.random() * 25;

      const boneX = x + Math.cos(angle) * distance;
      const boneY = y + Math.sin(angle) * distance;

      const skeleton = new KemikSavasci(boneX, boneY);
      skeleton.enemyTypeId = 'kemik_savasci';
      skeleton.spawnPosition = { x: boneX, y: boneY };
      skeleton.initEnemyAI('melee_aggro');
      skeleton.setTarget(this.player);
      skeleton.setMap(this.map);
      this.enemies.push(skeleton);
    }
  }

  private spawnErlikElcisiMinions(boss: ErlikElcisi): void {
    // Spawn cursed spirits and bone warriors as minions
    const minionCount = 2 + Math.floor(Math.random() * 2);

    for (let i = 0; i < minionCount; i++) {
      const angle = (i / minionCount) * Math.PI * 2;
      const distance = 80 + Math.random() * 40;

      const minionX = boss.position.x + Math.cos(angle) * distance;
      const minionY = boss.position.y + Math.sin(angle) * distance;

      // Alternate between bone warriors and cursed spirits
      const isBone = i % 2 === 0;
      const minion = isBone
        ? new KemikSavasci(minionX, minionY)
        : new LanetliRuh(minionX, minionY);
      minion.enemyTypeId = isBone ? 'kemik_savasci' : 'lanetli_ruh';
      minion.spawnPosition = { x: minionX, y: minionY };
      minion.initEnemyAI(isBone ? 'melee_aggro' : 'caster_aoe', { preferredRange: 120, leashRange: 9999 });

      minion.setTarget(this.player);
      minion.setMap(this.map);
      this.enemies.push(minion);
    }
  }

  private spawnErlikHanMinions(boss: ErlikHan): void {
    // Spawn elite minions for the final boss
    const minionCount = 3 + Math.floor(Math.random() * 2);

    for (let i = 0; i < minionCount; i++) {
      const angle = (i / minionCount) * Math.PI * 2;
      const distance = 100 + Math.random() * 50;

      const minionX = boss.position.x + Math.cos(angle) * distance;
      const minionY = boss.position.y + Math.sin(angle) * distance;

      // Mix of knights and seraphs
      const isKnight = i % 2 === 0;
      const minion = isKnight
        ? new ErlikSovalyesi(minionX, minionY)
        : new KaranlikSeraf(minionX, minionY);
      minion.enemyTypeId = isKnight ? 'erlik_sovalyesi' : 'karanlik_seraf';
      minion.spawnPosition = { x: minionX, y: minionY };
      minion.initEnemyAI(
        isKnight ? 'melee_aggro' : 'ranged_kite',
        { preferredRange: isKnight ? 40 : 170, leashRange: 9999 },
      );

      minion.setTarget(this.player);
      minion.setMap(this.map);
      this.enemies.push(minion);
    }
  }

  private spawnBossMinions(boss: ErlikGuard): void {
    // Spawn 2-3 minions near the boss
    const minionCount = 2 + Math.floor(Math.random() * 2);

    for (let i = 0; i < minionCount; i++) {
      const angle = (Math.PI * 2 / minionCount) * i;
      const distance = 50 + Math.random() * 30;

      const x = boss.position.x + Math.cos(angle) * distance;
      const y = boss.position.y + Math.sin(angle) * distance;

      const minion = new Kormos(x, y);
      minion.enemyTypeId = 'kormos';
      minion.setTarget(this.player);
      minion.setMap(this.map);
      this.enemies.push(minion);
    }
  }

  private spawnAlbastiMinions(boss: AlbastiKralicesi): void {
    // Phase-based summoning: Phase 1 = albasti, Phase 2 = karabasan, Phase 3 = no summons
    const summonType = boss.getSummonType();
    const minionCount = 2; // 2 per summon, max 6 on field

    // Count existing boss minions to respect cap
    const existingMinions = this.enemies.filter(e => e.isAlive && (e.enemyTypeId === 'albasti' || e.enemyTypeId === 'karabasan') && e.rarity !== 'boss').length;
    if (existingMinions >= 6) return;

    const toSpawn = Math.min(minionCount, 6 - existingMinions);

    for (let i = 0; i < toSpawn; i++) {
      const angle = (Math.PI * 2 / toSpawn) * i;
      const distance = 60 + Math.random() * 40;

      const x = boss.position.x + Math.cos(angle) * distance;
      const y = boss.position.y + Math.sin(angle) * distance;

      const minion = summonType === 'karabasan' ? new Karabasan(x, y) : new Albasti(x, y);
      minion.enemyTypeId = summonType === 'karabasan' ? 'karabasan' : 'albasti';
      minion.setTarget(this.player);
      minion.setMap(this.map);
      this.enemies.push(minion);
    }

    // Visual effect for summoning
    this.particleSystem.emitMagic(boss.position.x, boss.position.y, '#8b0000');
    this.screenShake.shakeBossAbility();
  }

  private spawnYerSubMinions(boss: YerSub): void {
    // Yer-Sub summons elemental minions (rock golems and wind spirits)
    const minionCount = 2 + Math.floor(Math.random() * 2);

    for (let i = 0; i < minionCount; i++) {
      const angle = (Math.PI * 2 / minionCount) * i;
      const distance = 70 + Math.random() * 50;

      const x = boss.position.x + Math.cos(angle) * distance;
      const y = boss.position.y + Math.sin(angle) * distance;

      // Mix of elemental minions
      const roll = Math.random();
      let minion: Enemy;
      let typeId: string;
      if (roll < 0.4) {
        minion = new KayaGolemi(x, y);
        typeId = 'kaya_golemi';
      } else if (roll < 0.7) {
        minion = new RuzgarRuhu(x, y);
        typeId = 'ruzgar_ruhu';
      } else {
        minion = new DemirKurt(x, y);
        typeId = 'demir_kurt';
      }
      minion.enemyTypeId = typeId;
      minion.setTarget(this.player);
      minion.setMap(this.map);
      this.enemies.push(minion);
    }

    // Visual effect for summoning
    this.particleSystem.emitMagic(boss.position.x, boss.position.y, '#4a7c59');
    this.screenShake.shakeBossAbility();
  }

  /**
   * Process boss attack results from BossAI system.
   * Applies damage, AOE, knockback, status effects, spawns minions,
   * and handles phase transition effects.
   */
  private processBossAttackResults(): void {
    for (const enemy of this.enemies) {
      if (!enemy.isAlive || !enemy.bossAI) continue;

      // --- Phase transition effects ---
      if (enemy.bossAI.phaseTransitionThisFrame) {
        const phaseIdx = enemy.bossAI.newPhaseIndex;
        // Screen shake + flash for phase transition
        this.screenShake.shakeBossAbility();
        this.screenShake.triggerFlash('#ff44ff', 0.15, 0.3);
        // Burst of particles at boss position
        this.particleSystem.emitExplosion(enemy.position.x, enemy.position.y, 80);
        if (phaseIdx >= 2) {
          // Enrage phase: extra effects
          this.screenShake.shakeExplosion();
          this.particleSystem.emitExplosion(enemy.position.x, enemy.position.y, 120);
        }
        // Phase announcement text
        const phaseLabels = ['I', 'II', 'III', 'IV'];
        const label = phaseLabels[phaseIdx] || `${phaseIdx + 1}`;
        this.phaseAnnounce = { active: true, text: `Phase ${label}`, timer: 0, duration: 2.0 };
      }

      // --- Process attack result ---
      const result: BossAttackResult | null = enemy.lastBossAttackResult;
      if (!result) continue;

      const player = this.player;
      if (!player.isAlive) continue;

      // Check if attack has AOE
      if (result.aoeRadius && result.aoePosition) {
        // AOE attack: check if player is within radius
        const dx = player.position.x - result.aoePosition.x;
        const dy = player.position.y - result.aoePosition.y;
        const distSq = dx * dx + dy * dy;
        const radiusSq = result.aoeRadius * result.aoeRadius;

        if (distSq <= radiusSq && result.damage > 0) {
          player.takeDamage(result.damage, 'physical', enemy.position);
          // Screen shake + hit-stop for boss attacks
          if (result.damage >= 50) {
            this.screenShake.shakeHeavyHit();
            this.screenShake.triggerHitStop(0.06);
          } else {
            this.screenShake.shakeHit();
            this.screenShake.triggerHitStop(0.03);
          }
          this.particleSystem.emitBlood(player.position.x, player.position.y - player.height / 2);
        }

        // AOE particle effect at impact position
        if (result.attackId === 'poison_pool') {
          this.particleSystem.emitPoison(result.aoePosition.x, result.aoePosition.y);
          this.particleSystem.emitBurst(result.aoePosition.x, result.aoePosition.y, '#44ff44', 15);
        } else if (result.attackId === 'shadow_barrage') {
          this.particleSystem.emitMagic(result.aoePosition.x, result.aoePosition.y, '#aa44ff');
        } else if (result.attackId === 'ground_slam') {
          this.particleSystem.emitExplosion(result.aoePosition.x, result.aoePosition.y, result.aoeRadius * 0.6);
          this.screenShake.shakeExplosion();
        } else {
          this.particleSystem.emitBurst(result.aoePosition.x, result.aoePosition.y, '#ff6644', 10);
        }
      } else if (result.damage > 0) {
        // Direct (non-AOE) attack: always hits if boss is in melee range
        const dx = player.position.x - enemy.position.x;
        const dy = player.position.y - enemy.position.y;
        const distSq = dx * dx + dy * dy;
        const meleeRange = 80;
        if (distSq <= meleeRange * meleeRange) {
          player.takeDamage(result.damage, 'physical', enemy.position);
          this.screenShake.shakeHit();
          this.screenShake.triggerHitStop(0.03);
          this.particleSystem.emitBlood(player.position.x, player.position.y - player.height / 2);
        }
      }

      // Apply status effect - map boss status strings to StatusEffectConfig
      if (result.statusEffect) {
        const statusMap: Record<string, import('@shared/effects/StatusEffect').StatusEffectConfig> = {
          poison: { type: 'poison', duration: 4.0, baseDamage: 8, stacks: 3 },
          burn: { type: 'burn', duration: 3.0, baseDamage: 15 },
          kut_burn: { type: 'burn', duration: 5.0, baseDamage: 20 },
          stun: { type: 'freeze', duration: 1.5 },
          fear: { type: 'chill', duration: 3.0, slowPercent: 60 },
        };
        const config = statusMap[result.statusEffect];
        if (config) {
          player.applyStatusEffect(config, enemy.id);
        }
      }

      // Spawn minions from BossAI (separate from the class-specific shouldSummon system)
      if (result.spawnMinions) {
        for (const minionDef of result.spawnMinions) {
          for (let j = 0; j < minionDef.count; j++) {
            const angle = (Math.PI * 2 / minionDef.count) * j;
            const mx = enemy.position.x + Math.cos(angle) * minionDef.offsetRadius;
            const my = enemy.position.y + Math.sin(angle) * minionDef.offsetRadius;
            const minion = this.createEnemyByType(minionDef.typeId as import('@shared/world/Zone').EnemySpawnType, mx, my);
            if (minion) {
              minion.setTarget(this.player);
              minion.setMap(this.map);
              this.enemies.push(minion);
            }
          }
          // Visual effect
          this.particleSystem.emitMagic(enemy.position.x, enemy.position.y, '#9944ff');
          this.screenShake.shakeHit();
        }
      }
    }
  }

  /**
   * Render boss attack telegraphs (ground warning indicators).
   * Called from render() after terrain but before entities.
   */
  renderBossTelegraphs(ctx: CanvasRenderingContext2D, camera: Camera): void {
    for (const enemy of this.enemies) {
      if (!enemy.isAlive || !enemy.bossAI) continue;

      for (const tele of enemy.bossAI.activeTelegraphs) {
        const progress = tele.elapsed / tele.duration; // 0→1
        const screenPos = camera.worldToScreen(tele.position.x, tele.position.y);
        const screenRadius = tele.radius * camera.zoom;

        // Pulsing fill (grows more opaque as telegraph completes)
        const fillAlpha = 0.05 + progress * 0.2;
        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y, screenRadius * progress, 0, Math.PI * 2);
        ctx.fillStyle = tele.color;
        ctx.globalAlpha = fillAlpha;
        ctx.fill();

        // Outer ring (always visible, pulses)
        const pulse = 0.6 + Math.sin(tele.elapsed * 12) * 0.4;
        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y, screenRadius, 0, Math.PI * 2);
        ctx.strokeStyle = tele.color;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.3 + progress * 0.5 * pulse;
        ctx.stroke();

        // Inner shrinking ring (countdown visual)
        const innerRadius = screenRadius * (1 - progress);
        if (innerRadius > 2) {
          ctx.beginPath();
          ctx.arc(screenPos.x, screenPos.y, innerRadius, 0, Math.PI * 2);
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 1.5;
          ctx.globalAlpha = 0.4 + progress * 0.4;
          ctx.stroke();
        }

        ctx.globalAlpha = 1;
      }
    }
  }

  update(deltaTime: number): void {
    // Update effects systems
    this.particleSystem.update(deltaTime);
    getDamageNumbers().update(deltaTime);
    this.screenShake.update(deltaTime);
    this._lootFilter!.setPlayerLevel(this.player.level);
    this._lootFilter!.update(deltaTime);
    this.weatherSystem.update(deltaTime);

    // Ambient particles - throttled to every 100ms instead of every frame
    this._ambientTimer += deltaTime;
    if (this._ambientTimer >= 0.1) {
      this._ambientTimer = 0;
      const camPos = this.camera.getPosition();
      this.particleSystem.emitAmbient(this.currentZoneConfig.id, camPos.x, camPos.y);
    }

    // Update map (for animated props)
    this.map.update(deltaTime);

    // Update thrust dash animation (with walkability check)
    if (this._thrustDash) {
      this._thrustDash.elapsed += deltaTime;
      const t = Math.min(1, this._thrustDash.elapsed / this._thrustDash.duration);
      const eased = 1 - (1 - t) * (1 - t);
      const nextX = this._thrustDash.startX + (this._thrustDash.endX - this._thrustDash.startX) * eased;
      const nextY = this._thrustDash.startY + (this._thrustDash.endY - this._thrustDash.startY) * eased;

      if (this.map.isWorldPositionWalkable(nextX, nextY)) {
        this.player.position.x = nextX;
        this.player.position.y = nextY;
      } else {
        // Hit a wall - stop dash
        this._thrustDash = null;
      }

      if (t >= 1) {
        this._thrustDash = null;
      }
    }

    // Update player
    this.player.update(deltaTime);

    // Update NPCs
    for (const npc of this.npcs) {
      npc.update(deltaTime);
    }
    this.updateNPCHighlights();

    // Update waypoint
    if (this.waypoint) {
      this.waypoint.update(deltaTime);
    }

    // Update camera with screen shake
    this.camera.update(deltaTime);
    this.camera.setShakeOffset(
      this.screenShake.getOffsetX(),
      this.screenShake.getOffsetY()
    );

    // Update enemies — skip full AI for enemies far from the player (>1500 world units)
    const playerX = this.player.position.x;
    const playerY = this.player.position.y;
    const AI_CULL_DIST_SQ = 1500 * 1500; // squared distance threshold
    for (const enemy of this.enemies) {
      const dx = enemy.position.x - playerX;
      const dy = enemy.position.y - playerY;
      const distSq = dx * dx + dy * dy;
      if (distSq > AI_CULL_DIST_SQ && enemy.isAlive && !enemy.isDying) {
        // Far-away enemies: only tick death/status timers, skip AI/movement
        enemy.updateTimers(deltaTime);
        continue;
      }
      enemy.update(deltaTime);
    }

    // --- Process boss attack results (BossAI outputs) ---
    this.processBossAttackResults();

    // Remove dead enemies and spawn loot (swap-and-pop to avoid array allocation)
    const triggerManager = getTriggerEffectManager();
    const playerCalc = this.player.getStatCalculator();

    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const enemy = this.enemies[i];
      if (!enemy.isAlive && !enemy.isDying) {
        // Track kills
        this.totalKills++;

        // Death effects
        this.particleSystem.emitDeath(enemy.position.x, enemy.position.y, enemy.color);

        // Determine enemy type for quest tracking
        const isBoss = enemy.rarity === 'boss';
        const enemyType = this.getEnemyType(enemy);

        // Process on_kill trigger effects
        const killEventData: TriggerEventData = {
          sourceEntityId: this.player.id,
          targetEntityId: enemy.id,
          positionX: enemy.position.x,
          positionY: enemy.position.y,
          attackerHpPercent: this.player.getHealthPercent() * 100,
        };

        const killEffects = triggerManager.processTrigger('on_kill', playerCalc, killEventData);
        for (const effectResult of killEffects) {
          const resolved = resolveEffectAction(effectResult, 0, enemy.position);
          this.applyEffectResult(resolved);
        }

        // Trigger quest callback
        if (this.onEnemyKilled) {
          this.onEnemyKilled(enemyType, isBoss);
        }

        // Check if boss was killed
        if (isBoss) {
          this._activeBoss = null;
          this.bossesDefeated++;
          this.screenShake.shakeBossAbility(); // Big shake for boss death
          this.particleSystem.emitExplosion(enemy.position.x, enemy.position.y, 100);

          if (enemy instanceof AlbastiKralicesi) {
            debugLog('GameWorld',` ACT 1 COMPLETE! Albasti Kralicesi defeated!`);
            this.particleSystem.emitExplosion(enemy.position.x, enemy.position.y, 150);
          } else if (enemy instanceof YerSub) {
            debugLog('GameWorld',` ACT 2 COMPLETE! Yer-Sub defeated!`);
            this.particleSystem.emitExplosion(enemy.position.x, enemy.position.y, 150);
            this.particleSystem.emitLevelUp(enemy.position.x, enemy.position.y);
          } else if (enemy instanceof ErlikElcisi) {
            debugLog('GameWorld',` ACT 3 COMPLETE! Erlik Elcisi defeated!`);
            this.particleSystem.emitExplosion(enemy.position.x, enemy.position.y, 180);
            this.particleSystem.emitLevelUp(enemy.position.x, enemy.position.y);
            this.particleSystem.emitLevelUp(enemy.position.x + 50, enemy.position.y);
          } else if (enemy instanceof ErlikHan) {
            debugLog('GameWorld',` GAME COMPLETE! Erlik Han defeated!`);
            const deathX = enemy.position.x;
            const deathY = enemy.position.y;
            const ps = this.particleSystem;
            for (let i = 0; i < 5; i++) {
              const tid = setTimeout(() => {
                ps.emitExplosion(
                  deathX + (Math.random() - 0.5) * 200,
                  deathY + (Math.random() - 0.5) * 200,
                  200
                );
                ps.emitLevelUp(
                  deathX + (Math.random() - 0.5) * 150,
                  deathY + (Math.random() - 0.5) * 150
                );
              }, i * 200);
              this.pendingTimeouts.push(tid);
            }
          } else {
            debugLog('GameWorld',` Boss defeated: ${enemy.name}`);
          }
        }

        // Check for elite on-death effects
        if (enemy instanceof AlKormos) {
          const deathEffect = enemy.getOnDeathEffect();
          if (deathEffect === 'explode') {
            this.particleSystem.emitExplosion(enemy.position.x, enemy.position.y, 80);
            this.screenShake.shakeExplosion();
            // Damage player if nearby (squared distance avoids sqrt)
            const dx = this.player.position.x - enemy.position.x;
            const dy = this.player.position.y - enemy.position.y;
            const distSq = dx * dx + dy * dy;
            if (distSq < 100 * 100) {
              const damage = this.player.takeDamage(30);
              if (damage > 0) {
                this.particleSystem.emitBlood(this.player.position.x, this.player.position.y - this.player.height / 2);
                getDamageNumbers().spawn(
                  this.player.position.x,
                  this.player.position.y - this.player.height,
                  damage,
                  'physical'
                );
                getAudioManager().play(Math.random() < 0.5 ? 'hit_soft' : 'hit_soft_1');
              }
            }
          }
        }

        // Award experience
        const leveledUp = this.player.addExperience(enemy.expReward);

        getDamageNumbers().spawn(
          enemy.position.x,
          enemy.position.y - 30,
          enemy.expReward,
          'xp'
        );

        // Level up celebration effects
        if (leveledUp) {
          this.particleSystem.emitLevelUp(this.player.position.x, this.player.position.y);
          this.screenShake.shakeLevelUp();
          getDamageNumbers().spawn(
            this.player.position.x,
            this.player.position.y - this.player.height,
            t('combat.levelUp'),
            'level_up'
          );
        }

        // Spawn loot
        this.spawnLoot(enemy);

        // Swap-and-pop removal — O(1)
        this.enemies[i] = this.enemies[this.enemies.length - 1];
        this.enemies.pop();
      }
    }

    // Check for boss summon ability
    for (const enemy of this.enemies) {
      if (enemy instanceof ErlikGuard && enemy.shouldSummon()) {
        this.spawnBossMinions(enemy);
      }
      if (enemy instanceof AlbastiKralicesi && enemy.shouldSummon()) {
        this.spawnAlbastiMinions(enemy);
      }
      if (enemy instanceof YerSub && enemy.shouldSummon()) {
        this.spawnYerSubMinions(enemy);
      }
      if (enemy instanceof ErlikElcisi && enemy.shouldSummon()) {
        this.spawnErlikElcisiMinions(enemy);
      }
      if (enemy instanceof ErlikHan && enemy.shouldSummon()) {
        this.spawnErlikHanMinions(enemy);
      }
    }

    // Update dropped items
    for (const item of this.droppedItems) {
      item.update(deltaTime);
    }

    // Check for item pickups (auto-pickup nearby items)
    this.handleItemPickup();

    // No periodic spawning - all enemies spawn at zone start

    // Update skill effects (swap-and-pop to avoid array allocation)
    for (let i = this.skillEffects.length - 1; i >= 0; i--) {
      this.skillEffects[i].update(deltaTime);
      if (this.skillEffects[i].isComplete) {
        this.skillEffects[i] = this.skillEffects[this.skillEffects.length - 1];
        this.skillEffects.pop();
      }
    }

    // Update V2 beam effects (swap-and-pop)
    for (let i = this.beamEffects.length - 1; i >= 0; i--) {
      const beam = this.beamEffects[i];
      beam.update(deltaTime);
      if (!beam.isComplete) {
        beam.updateOrigin(this.player.position);
        this.applyBeamDamage(beam, deltaTime);
      } else {
        this.beamEffects[i] = this.beamEffects[this.beamEffects.length - 1];
        this.beamEffects.pop();
      }
    }

    // Aura damage tick timer (1 second interval)
    this.auraDamageTimer -= deltaTime;
    if (this.auraDamageTimer < 0) this.auraDamageTimer = 1.0;

    // Update V2 aura effects (swap-and-pop)
    for (let i = this.auraEffects.length - 1; i >= 0; i--) {
      const aura = this.auraEffects[i];
      aura.updatePosition(this.player.position);
      aura.update(deltaTime);
      if (!aura.isComplete) {
        this.applyAuraEffects(aura);
      } else {
        this.auraEffects[i] = this.auraEffects[this.auraEffects.length - 1];
        this.auraEffects.pop();
      }
    }

    // Update V2 traps (swap-and-pop)
    for (let i = this.traps.length - 1; i >= 0; i--) {
      const trap = this.traps[i];
      trap.update(deltaTime);
      if (trap.isArmed()) {
        if (trap.getTrapTrigger() === 'timed') {
          // Timed traps auto-trigger in their own update - check if triggered
          if (trap.getState() === 'triggered' || trap.getState() === 'exploded') {
            this.applyTrapExplosion(trap);
          }
        } else {
          // Proximity (default): trigger when enemy enters radius
          for (const enemy of this.enemies) {
            const enemyRadius = enemy.getHitboxRadius();
            if (enemy.isAlive && !enemy.isDying && trap.checkTrigger(enemy.position.x, enemy.position.y, enemyRadius)) {
              this.applyTrapExplosion(trap);
              break;
            }
          }
        }
      }
      if (!trap.isAlive) {
        this.traps[i] = this.traps[this.traps.length - 1];
        this.traps.pop();
      }
    }

    // Update V2 totems (swap-and-pop)
    for (let i = this.totems.length - 1; i >= 0; i--) {
      const totem = this.totems[i];
      totem.update(deltaTime);
      if (totem.isAlive) {
        this.processTotemAttack(totem);
      } else {
        this.totems[i] = this.totems[this.totems.length - 1];
        this.totems.pop();
      }
    }

    // Update minions
    for (const minion of this.minions) {
      minion.update(deltaTime, this.enemies);
      if (minion.isAlive) {
        this.processMinionAttack(minion);
      }
    }

    // Update HUD with boss info
    this.updateHUDBossInfo();

    // Update zone name animation
    if (this.zoneNameAnim.active) {
      this.zoneNameAnim.timer += deltaTime;
      if (this.zoneNameAnim.timer >= this.zoneNameAnim.duration) {
        this.zoneNameAnim.active = false;
      }
    }

    // Update boss phase announcement
    if (this.phaseAnnounce.active) {
      this.phaseAnnounce.timer += deltaTime;
      if (this.phaseAnnounce.timer >= this.phaseAnnounce.duration) {
        this.phaseAnnounce.active = false;
      }
    }

    // Update boss cinematic
    if (this.bossCinematic.active) {
      this.updateBossCinematic(deltaTime);
    }
  }

  private updateHUDBossInfo(): void {
    // Use cached boss reference instead of iterating all enemies
    let bossName: string | undefined;
    let bossActive = false;

    // Validate cached ref is still alive; clear if dead
    if (this._activeBoss && (!this._activeBoss.isAlive || this._activeBoss.isDying)) {
      this._activeBoss = null;
    }

    if (this._activeBoss) {
      bossName = this._activeBoss.name;
      bossActive = true;
    }

    // Trigger cinematic when boss is first encountered (only once per boss)
    if (bossActive && !this.wasBossActive) {
      const bossId = bossName || 'BOSS';
      if (!this.bossCinematic.encounteredBosses.has(bossId)) {
        this.bossCinematic.encounteredBosses.add(bossId);
        this.camera.bossEncounterZoom();
        this.screenShake.shakeBossAbility();

        // Resolve boss title / epithet based on known bosses
        let title = '';
        if (bossId.toLowerCase().includes('albast')) {
          title = 'Zehir Krali\u00E7esi';
        } else if (bossId.toLowerCase().includes('karabasan')) {
          title = 'Kabus Efendisi';
        } else if (bossId.toLowerCase().includes('dag') || bossId.toLowerCase().includes('devi')) {
          title = 'Da\u011F\u0131n Gazab\u0131';
        }

        // Start boss cinematic
        const prev = this.bossCinematic.encounteredBosses; // preserve set across reset
        this.bossCinematic = {
          active: true,
          timer: 0,
          duration: 3.8,
          bossName: bossId,
          bossTitle: title,
          letterboxProgress: 0,
          nameAlpha: 0,
          titleAlpha: 0,
          vignetteAlpha: 0,
          shockwaveProgress: 0,
          phase: 'letterbox_in',
          encounteredBosses: prev,
        };
      }
    }
    this.wasBossActive = bossActive;

    // TODO: boss bar in HudHTML
  }

  private updateBossCinematic(deltaTime: number): void {
    const c = this.bossCinematic;
    c.timer += deltaTime;

    // Phase timings (extended for more dramatic effect)
    const letterboxInEnd = 0.5;
    const nameShowEnd = 2.8;
    const letterboxOutEnd = 3.8;

    if (c.timer < letterboxInEnd) {
      // Phase 1: Letterbox bars slide in with vignette and shockwave
      c.phase = 'letterbox_in';
      const t = c.timer / letterboxInEnd;
      // Ease-out cubic for smooth deceleration
      c.letterboxProgress = 1 - Math.pow(1 - t, 3);
      c.vignetteAlpha = t * 0.5;
      c.shockwaveProgress = t;
      c.nameAlpha = 0;
      c.titleAlpha = 0;
    } else if (c.timer < nameShowEnd) {
      // Phase 2: Name + title display with staggered fade
      c.phase = 'name_show';
      c.letterboxProgress = 1;
      c.shockwaveProgress = 1;
      const nameTime = c.timer - letterboxInEnd;
      const nameDuration = nameShowEnd - letterboxInEnd; // 2.3s

      // Boss name fades in first
      if (nameTime < 0.35) {
        c.nameAlpha = nameTime / 0.35;
        c.titleAlpha = 0;
      } else if (nameTime < 0.6) {
        c.nameAlpha = 1;
        // Title fades in with slight delay after name
        c.titleAlpha = (nameTime - 0.35) / 0.25;
      } else if (nameTime > nameDuration - 0.5) {
        // Both fade out together
        const fadeOut = (nameDuration - nameTime) / 0.5;
        c.nameAlpha = fadeOut;
        c.titleAlpha = fadeOut;
      } else {
        c.nameAlpha = 1;
        c.titleAlpha = 1;
      }

      // Vignette holds steady during name display
      c.vignetteAlpha = 0.5;
    } else if (c.timer < letterboxOutEnd) {
      // Phase 3: Letterbox bars slide out, vignette fades
      c.phase = 'letterbox_out';
      const outTime = c.timer - nameShowEnd;
      const outDuration = letterboxOutEnd - nameShowEnd; // 1.0s
      // Ease-in cubic for accelerating exit
      const t = outTime / outDuration;
      c.letterboxProgress = 1 - t * t * t;
      c.vignetteAlpha = 0.5 * (1 - t);
      c.nameAlpha = 0;
      c.titleAlpha = 0;
      c.shockwaveProgress = 1;
    } else {
      // Done
      c.active = false;
      c.phase = 'done';
      c.vignetteAlpha = 0;
      c.shockwaveProgress = 0;
    }
  }

  private renderBossCinematic(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    const c = this.bossCinematic;
    if (!c.active) return;

    // Invalidate cached cinematic gradients on resize
    if (width !== this._bossCineW || height !== this._bossCineH) {
      this._bossVigGrad = null;
      this._bossGlowGrad = null;
      this._bossTopBarGrad = null;
      this._bossBotBarGrad = null;
      this._bossCineW = width;
      this._bossCineH = height;
    }

    // -- Dark vignette overlay --
    if (c.vignetteAlpha > 0.01) {
      if (!this._bossVigGrad) {
        this._bossVigGrad = ctx.createRadialGradient(
          width / 2, height / 2, Math.min(width, height) * 0.25,
          width / 2, height / 2, Math.max(width, height) * 0.75,
        );
        this._bossVigGrad.addColorStop(0, 'rgba(0, 0, 0, 0)');
        this._bossVigGrad.addColorStop(1, 'rgba(0, 0, 0, 1)');
      }
      ctx.save();
      ctx.globalAlpha = c.vignetteAlpha;
      ctx.fillStyle = this._bossVigGrad;
      ctx.fillRect(0, 0, width, height);
      ctx.restore();
    }

    // -- Expanding shockwave ring --
    if (c.shockwaveProgress > 0.01 && c.shockwaveProgress < 1.0) {
      ctx.save();
      const maxRadius = Math.max(width, height) * 0.6;
      const radius = c.shockwaveProgress * maxRadius;
      const ringAlpha = (1 - c.shockwaveProgress) * 0.35;
      ctx.strokeStyle = `rgba(255, 215, 0, ${ringAlpha})`;
      ctx.lineWidth = 3 - c.shockwaveProgress * 2;
      ctx.beginPath();
      ctx.arc(width / 2, height / 2, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // -- Letterbox bars (gradient cached per bar height) --
    const barHeight = 70 * c.letterboxProgress;
    const barHRound = (barHeight + 0.5) | 0;

    if (barHRound > 0) {
      if (!this._bossTopBarGrad || this._bossCineBarH !== barHRound) {
        this._bossTopBarGrad = ctx.createLinearGradient(0, 0, 0, barHRound);
        this._bossTopBarGrad.addColorStop(0, '#000000');
        this._bossTopBarGrad.addColorStop(1, 'rgba(20, 0, 0, 0.95)');
        this._bossBotBarGrad = ctx.createLinearGradient(0, height - barHRound, 0, height);
        this._bossBotBarGrad.addColorStop(0, 'rgba(20, 0, 0, 0.95)');
        this._bossBotBarGrad.addColorStop(1, '#000000');
        this._bossCineBarH = barHRound;
      }
      ctx.fillStyle = this._bossTopBarGrad;
      ctx.fillRect(0, 0, width, barHeight);
      ctx.fillStyle = this._bossBotBarGrad!;
      ctx.fillRect(0, height - barHeight, width, barHeight);
    }

    // Gold trim on letterbox edges (pulsing glow)
    if (c.letterboxProgress > 0.1) {
      const pulse = 0.6 + 0.4 * Math.sin(c.timer * 3.0);
      const trimAlpha = c.letterboxProgress * pulse;
      ctx.fillStyle = `rgba(255, 215, 0, ${trimAlpha * 0.7})`;
      ctx.fillRect(0, barHeight - 2, width, 2);
      ctx.fillRect(0, height - barHeight, width, 2);

      // Secondary thinner crimson line
      ctx.fillStyle = `rgba(180, 30, 30, ${trimAlpha * 0.5})`;
      ctx.fillRect(0, barHeight - 4, width, 1);
      ctx.fillRect(0, height - barHeight + 2, width, 1);
    }

    // -- Boss name display --
    if (c.nameAlpha > 0.01) {
      ctx.save();
      ctx.globalAlpha = c.nameAlpha;

      // Background glow behind text (cached radial gradient)
      if (!this._bossGlowGrad) {
        this._bossGlowGrad = ctx.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, 280);
        this._bossGlowGrad.addColorStop(0, 'rgba(139, 0, 0, 0.5)');
        this._bossGlowGrad.addColorStop(0.5, 'rgba(80, 0, 0, 0.25)');
        this._bossGlowGrad.addColorStop(1, 'rgba(139, 0, 0, 0)');
      }
      ctx.fillStyle = this._bossGlowGrad;
      ctx.fillRect(width / 2 - 300, height / 2 - 70, 600, 140);

      // Boss name - large dramatic text
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Deep shadow layers for depth
      ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
      ctx.font = 'bold 42px Arial';
      ctx.fillText(c.bossName, width / 2 + 3, height / 2 - 12);
      ctx.fillStyle = 'rgba(80, 0, 0, 0.6)';
      ctx.fillText(c.bossName, width / 2 + 1, height / 2 - 13);

      // Main text with gold color
      ctx.fillStyle = '#ffd700';
      ctx.font = 'bold 42px Arial';
      ctx.fillText(c.bossName, width / 2, height / 2 - 14);

      // Decorative lines flanking the name (dual lines)
      const nameWidth = ctx.measureText(c.bossName).width;
      const lineWidth = Math.min(nameWidth + 80, 400);
      const lineAlpha = c.nameAlpha * 0.85;

      const lineGrad = ctx.createLinearGradient(
        width / 2 - lineWidth / 2, 0,
        width / 2 + lineWidth / 2, 0,
      );
      lineGrad.addColorStop(0, `rgba(255, 215, 0, 0)`);
      lineGrad.addColorStop(0.15, `rgba(255, 215, 0, ${lineAlpha})`);
      lineGrad.addColorStop(0.85, `rgba(255, 215, 0, ${lineAlpha})`);
      lineGrad.addColorStop(1, `rgba(255, 215, 0, 0)`);
      ctx.fillStyle = lineGrad;
      ctx.fillRect(width / 2 - lineWidth / 2, height / 2 + 8, lineWidth, 2);

      // Second decorative line (thinner, slightly offset)
      const lineGrad2 = ctx.createLinearGradient(
        width / 2 - lineWidth * 0.4, 0,
        width / 2 + lineWidth * 0.4, 0,
      );
      lineGrad2.addColorStop(0, `rgba(180, 30, 30, 0)`);
      lineGrad2.addColorStop(0.2, `rgba(180, 30, 30, ${lineAlpha * 0.6})`);
      lineGrad2.addColorStop(0.8, `rgba(180, 30, 30, ${lineAlpha * 0.6})`);
      lineGrad2.addColorStop(1, `rgba(180, 30, 30, 0)`);
      ctx.fillStyle = lineGrad2;
      ctx.fillRect(width / 2 - lineWidth * 0.4, height / 2 + 12, lineWidth * 0.8, 1);

      ctx.restore();
    }

    // -- Boss title / epithet display (staggered fade after name) --
    if (c.bossTitle && c.titleAlpha > 0.01) {
      ctx.save();
      ctx.globalAlpha = c.titleAlpha;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Title shadow
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.font = 'italic 20px Arial';
      ctx.fillText(c.bossTitle, width / 2 + 1, height / 2 + 26);

      // Title text in muted silver-gold
      ctx.fillStyle = '#c8b06e';
      ctx.fillText(c.bossTitle, width / 2, height / 2 + 25);

      ctx.restore();
    }
  }

  private getEnemyType(enemy: Enemy): string {
    return enemy.enemyTypeId;
  }

  private spawnLoot(enemy: Enemy): void {
    const drops = enemy.lootTable.roll(enemy.itemLevel, 0);

    for (const item of drops) {
      const droppedItem = new DroppedItem(
        enemy.position.x,
        enemy.position.y,
        item
      );
      this.droppedItems.push(droppedItem);
    }

    // Quest item drops
    this.tryDropQuestItem(enemy);

    // Effect stone drops
    this.tryDropEffectStone(enemy);
  }

  private tryDropQuestItem(enemy: Enemy): void {
    // Karabasan drops ruh_damlasi (30% chance) when quest is active
    if (enemy instanceof Karabasan) {
      if (this.isQuestActive && this.isQuestActive('side_ruh_damlaları')) {
        if (Math.random() < 0.30) {
          const questItem = createQuestItem('ruh_damlasi');
          if (questItem) {
            const droppedItem = new DroppedItem(
              enemy.position.x + (Math.random() - 0.5) * 20,
              enemy.position.y + (Math.random() - 0.5) * 20,
              questItem
            );
            this.droppedItems.push(droppedItem);
          }
        }
      }
    }

    // Enemies in erlik_tapinagi zone drop erlik_idolu (25% chance) when quest is active
    if (enemy.zoneId === 'erlik_tapinagi') {
      if (this.isQuestActive && this.isQuestActive('side_kara_gulle_bilmecesi')) {
        if (Math.random() < 0.25) {
          const questItem = createQuestItem('erlik_idolu');
          if (questItem) {
            const droppedItem = new DroppedItem(
              enemy.position.x + (Math.random() - 0.5) * 20,
              enemy.position.y + (Math.random() - 0.5) * 20,
              questItem
            );
            this.droppedItems.push(droppedItem);
          }
        }
      }
    }
  }

  private tryDropEffectStone(enemy: Enemy): void {
    const isBoss = enemy.rarity === 'boss';
    const isElite = enemy.rarity === 'elite';

    const stone = rollEffectStoneDrop(enemy.itemLevel || 1, isBoss, isElite);
    if (stone) {
      // Add the stone directly to player inventory
      this.player.inventory.addEffectStone(stone);

      // Show floating text with rarity-colored feedback
      const rarityColor = stone.rarity === 'legendary' ? '#ff8844' :
                          stone.rarity === 'rare' ? '#ffff44' :
                          stone.rarity === 'magic' ? '#4488ff' : '#ffffff';
      this.particleSystem.addFloatingText(
        enemy.position.x,
        enemy.position.y - 40,
        stone.name,
        rarityColor,
        stone.rarity === 'rare' || stone.rarity === 'legendary'
      );
    }
  }

  /** Auto-pickup radius for gold/currency items (in world units) */
  private static readonly AUTO_GOLD_PICKUP_RADIUS = 80;

  private handleItemPickup(): void {
    for (let i = this.droppedItems.length - 1; i >= 0; i--) {
      const droppedItem = this.droppedItems[i];
      // Remove completed collections (swap-and-pop)
      if (droppedItem.isCollectComplete()) {
        this.droppedItems[i] = this.droppedItems[this.droppedItems.length - 1];
        this.droppedItems.pop();
        continue;
      }

      // Skip items already being collected
      if (droppedItem.isBeingCollected) {
        continue;
      }

      // Auto-pickup gold/currency within 80 world units
      const isCurrency = 'currencyType' in droppedItem.item;
      if (isCurrency) {
        const dx = droppedItem.position.x - this.player.position.x;
        const dy = droppedItem.position.y - this.player.position.y;
        const distSq = dx * dx + dy * dy;
        if (distSq <= GameWorld.AUTO_GOLD_PICKUP_RADIUS * GameWorld.AUTO_GOLD_PICKUP_RADIUS) {
          const pickedUp = this.player.pickupItem(droppedItem.item);
          if (pickedUp) {
            droppedItem.startCollect(this.player.position.x, this.player.position.y);

            // Gold pickup particles
            this.particleSystem.emit({
              position: { x: droppedItem.position.x, y: droppedItem.position.y },
              count: 10,
              spread: Math.PI / 2,
              direction: -Math.PI / 2,
              speed: { min: 60, max: 150 },
              life: { min: 0.4, max: 0.8 },
              size: { min: 2, max: 5 },
              sizeDecay: 3,
              color: ['#ffd700', '#ffaa00', '#fff4a0'],
              gravity: -120,
              alphaDecay: 1.5,
            });

            getDamageNumbers().spawn(droppedItem.position.x, droppedItem.position.y, droppedItem.getDisplayName(), 'gold');
            getAudioManager().play('gold_pickup');
            continue;
          }
        }
      }

      if (droppedItem.isInPickupRange(this.player.position.x, this.player.position.y)) {
        const pickedUp = this.player.pickupItem(droppedItem.item);
        if (pickedUp) {
          // Start collect animation
          droppedItem.startCollect(this.player.position.x, this.player.position.y);

          // Spawn pickup particles
          const isCurrency = 'currencyType' in droppedItem.item;
          if (isCurrency) {
            // Gold: golden particles rising upward toward HUD
            this.particleSystem.emit({
              position: { x: droppedItem.position.x, y: droppedItem.position.y },
              count: 10,
              spread: Math.PI / 2,
              direction: -Math.PI / 2,
              speed: { min: 60, max: 150 },
              life: { min: 0.4, max: 0.8 },
              size: { min: 2, max: 5 },
              sizeDecay: 3,
              color: ['#ffd700', '#ffaa00', '#fff4a0'],
              gravity: -120,
              alphaDecay: 1.5,
            });
          } else {
            // Equipment: particles in the item's rarity color
            const rarityColor = getItemColor(droppedItem.item.rarity);
            this.particleSystem.emit({
              position: { x: droppedItem.position.x, y: droppedItem.position.y },
              count: 8,
              spread: Math.PI * 2,
              direction: 0,
              speed: { min: 40, max: 120 },
              life: { min: 0.3, max: 0.6 },
              size: { min: 2, max: 5 },
              sizeDecay: 4,
              color: rarityColor,
              gravity: 80,
              alphaDecay: 1.8,
            });
          }

          // Show pickup text + sound
          getDamageNumbers().spawn(droppedItem.position.x, droppedItem.position.y, droppedItem.getDisplayName(), 'gold');
          getAudioManager().play(isCurrency ? 'gold_pickup' : 'item_pickup');

          // Add mini notification popup via LootFilter
          if (!isCurrency) {
            this._lootFilter!.addPickupNotification(
              droppedItem.item.name,
              droppedItem.item.rarity
            );
          }

          // Quest: notify item collected for collect_item objectives
          const item = droppedItem.item;
          if (item && 'itemType' in item && item.itemType === 'quest' && 'questId' in item && this.onItemCollected) {
            // Use the quest item template key (e.g., 'ruh_damlasi')
            const questItemTarget = item.name ? this.getQuestItemTarget(item.name) : null;
            if (questItemTarget) {
              this.onItemCollected(questItemTarget);
            }
          }
        }
      }
    }
  }

  /** Map quest item display names to their target IDs for quest objective tracking */
  private getQuestItemTarget(itemName: string): string | null {
    const nameToTarget: Record<string, string> = {
      'Ruh Damlas\u0131': 'ruh_damlasi',
      'Erlik \u0130dol\u00fc': 'erlik_idolu',
    };
    return nameToTarget[itemName] || null;
  }

  handlePlayerAttack(): void {
    const attackInfo = this.player.attack();
    if (!attackInfo) return;

    // Find enemies in attack range
    const hitEnemies = CombatSystem.getEntitiesInRadius(
      this.enemies,
      attackInfo.x,
      attackInfo.y,
      this.player.attackRange
    );

    // Get player's stat calculator for leech calculation
    const playerCalc = this.player.getStatCalculator();

    for (const enemy of hitEnemies) {
      if (!enemy.isAlive || enemy.isDying) continue;

      const combatContext: CombatContext = { attackerCalculator: playerCalc };
      const result = CombatSystem.calculateDamageWithModifiers(
        attackInfo.damage,
        'physical',
        combatContext,
        this.player.level
      );

      if (!result.isDodged && !result.isBlocked) {
        const actualDamage = enemy.takeDamage(result.damage, 'physical', result.bypassES);

        // Apply ailment from combat result
        if (result.ailment && result.ailment.applied) {
          enemy.applyStatusEffect({
            type: result.ailment.type,
            duration: STATUS_DURATIONS[result.ailment.type] || 3,
            baseDamage: result.ailment.damage,
          }, this.player.id);
        }

        // Calculate and apply life leech
        const lifeLeech = CombatSystem.calculateLeech(actualDamage, playerCalc, 'life');
        if (lifeLeech > 0) {
          this.player.heal(lifeLeech);
          getDamageNumbers().spawn(this.player.position.x, this.player.position.y - 40, lifeLeech, 'heal');
        }

        // Calculate and apply mana leech
        const manaLeech = CombatSystem.calculateLeech(actualDamage, playerCalc, 'mana');
        if (manaLeech > 0) {
          this.player.stats.mana = Math.min(
            this.player.getEffectiveMaxMana(),
            this.player.stats.mana + manaLeech
          );
        }

        // Particle effects
        this.particleSystem.emitHit(enemy.position.x, enemy.position.y - enemy.height / 2);
        getDamageNumbers().spawn(
          enemy.position.x,
          enemy.position.y - enemy.height,
          actualDamage,
          result.isCritical ? 'crit' : 'physical'
        );

        // Process legendary on_hit effects
        this.processLegendaryEffects('on_hit', enemy.position.x, enemy.position.y, actualDamage);

        // Screen shake + sound + hit feedback
        const audio = getAudioManager();
        if (result.isCritical) {
          this.screenShake.shakeHeavyHit();
          this.screenShake.triggerHitStop(0.05);
          this.screenShake.triggerFlash('#ffffff', 0.08, 0.2);
          this.particleSystem.emitBlood(enemy.position.x, enemy.position.y - enemy.height / 2);
          audio.play('hit_critical');
        } else {
          this.screenShake.shakeHit();
          this.screenShake.triggerHitStop(0.03);
          audio.play(Math.random() < 0.5 ? 'hit_physical' : 'hit_physical_1');
        }

        // Enemy death sound + on_kill legendary effects
        if (!enemy.isAlive || enemy.isDying) {
          audio.play('enemy_death');
          this.screenShake.triggerFlash('#ff4444', 0.12, 0.15);
          this.processLegendaryEffects('on_kill', enemy.position.x, enemy.position.y, actualDamage);
        }
      } else if (result.isDodged) {
        // Dodge text
        this.particleSystem.addMissText(enemy.position.x, enemy.position.y - enemy.height);
        getDamageNumbers().spawn(enemy.position.x, enemy.position.y - enemy.height, 0, 'dodge');
        getAudioManager().play(Math.random() < 0.5 ? 'slash' : 'slash_1');
      } else {
        // Block text
        this.particleSystem.addMissText(enemy.position.x, enemy.position.y - enemy.height);
        getDamageNumbers().spawn(enemy.position.x, enemy.position.y - enemy.height, 0, 'block');
        getAudioManager().play('block');
      }
    }
  }

  handleSkillAttack(skill: HotbarSkill, damage: number, targetPos: Vector2, isV2: boolean = false): void {
    // Get effective stats using appropriate SkillCreator
    let areaRadius: number;
    let range: number;
    let element: import('@shared/types').ElementType;
    let baseType: string;

    if (isV2 && isSkillV2(skill)) {
      areaRadius = SkillCreatorV2.getEffectiveArea(skill);
      range = SkillCreatorV2.getEffectiveRange(skill);
      element = SkillCreatorV2.getEffectiveElement(skill);
      baseType = skill.deliveryType; // V2 uses deliveryType
    } else {
      const v1Skill = skill as CustomSkill;
      areaRadius = SkillCreator.getEffectiveArea(v1Skill);
      range = SkillCreator.getEffectiveRange(v1Skill);
      element = SkillCreator.getEffectiveElement(v1Skill);
      baseType = v1Skill.baseType;
    }

    // V1 skill cast sound based on baseType
    {
      const castAudio = getAudioManager();
      if (MELEE_SKILL_TYPES.has(baseType)) {
        castAudio.play(Math.random() < 0.5 ? 'slash' : 'slash_1');
      } else if (PROJECTILE_SKILL_TYPES.has(baseType)) {
        castAudio.play('hit_wood');
      } else if (AOE_SKILL_TYPES.has(baseType)) {
        castAudio.play('hit_metal');
      } else if (INSTANT_SKILL_TYPES.has(baseType)) {
        castAudio.play('hit_metal_1');
      }
    }

    // Calculate direction from player to mouse
    const dx = targetPos.x - this.player.position.x;
    const dy = targetPos.y - this.player.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const direction = dist > 0
      ? { x: dx / dist, y: dy / dist }
      : this.player.getFacingVector();

    // Update player facing based on target (8-directional)
    if (dx !== 0 || dy !== 0) {
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);
      if (angle >= -22.5 && angle < 22.5) this.player.facing = 'right';
      else if (angle >= 22.5 && angle < 67.5) this.player.facing = 'down-right';
      else if (angle >= 67.5 && angle < 112.5) this.player.facing = 'down';
      else if (angle >= 112.5 && angle < 157.5) this.player.facing = 'down-left';
      else if (angle >= 157.5 || angle < -157.5) this.player.facing = 'left';
      else if (angle >= -157.5 && angle < -112.5) this.player.facing = 'up-left';
      else if (angle >= -112.5 && angle < -67.5) this.player.facing = 'up';
      else if (angle >= -67.5 && angle < -22.5) this.player.facing = 'up-right';
    }

    // Determine hit area based on skill base type (baseType already defined above)
    let hitEnemies: Enemy[] = [];

    // For collision-based projectiles, create projectile(s) with collision callback
    if (PROJECTILE_SKILL_TYPES.has(baseType)) {
      // Determine projectile count, pierce, and contact behavior
      let projectileCount = 1;
      let pierceCount = 0;
      let projectileContact: 'none' | 'pierce' | 'chain' | 'fork' = 'none';
      let forkCount = 0;
      if (isV2 && isSkillV2(skill)) {
        projectileCount = SkillCreatorV2.getProjectileCount(skill);
        projectileContact = skill.projectileContact || 'none';
        if (projectileContact === 'none') {
          pierceCount = 0;
        } else if (projectileContact === 'pierce') {
          pierceCount = SkillCreatorV2.getPierceCount(skill);
        } else if (projectileContact === 'chain') {
          pierceCount = 0;
        } else if (projectileContact === 'fork') {
          forkCount = (skill.sliders as any).forkCount || 2;
          pierceCount = 0;
        }
      }

      // Build the onHit callback (captures current combat state)
      const playerCalc = this.player.getStatCalculator();
      const triggerManager = getTriggerEffectManager();
      const auraBonus = this.player.getAuraDamageBonus(element);
      const auraBoostedDamage = Math.floor(damage * (1 + auraBonus));
      const combatContext: CombatContext = {
        attackerCalculator: playerCalc,
        isSpell: true,
      };

      const onHitEnemy = (enemy: Enemy) => {
        if (!enemy.isAlive || enemy.isDying) return;

        const result = CombatSystem.calculateDamageWithModifiers(
          auraBoostedDamage,
          element,
          combatContext,
          this.player.level
        );

        if (!result.isDodged) {
          const actualDamage = enemy.takeDamage(result.damage, element, result.bypassES);

          // Apply ailment from combat result
          if (result.ailment && result.ailment.applied) {
            enemy.applyStatusEffect({
              type: result.ailment.type,
              duration: STATUS_DURATIONS[result.ailment.type] || 3,
              baseDamage: result.ailment.damage,
            }, this.player.id);
          }

          const hitEventData: TriggerEventData = {
            sourceEntityId: this.player.id,
            targetEntityId: enemy.id,
            damageDealt: actualDamage,
            damageType: element,
            isCritical: result.isCritical,
            positionX: enemy.position.x,
            positionY: enemy.position.y,
            skillId: skill.id,
            skillElement: element,
            skillBaseType: baseType,
            attackerHpPercent: this.player.getHealthPercent() * 100,
          };

          const hitEffects = triggerManager.processTrigger('on_hit', playerCalc, hitEventData);
          for (const effectResult of hitEffects) {
            const resolved = resolveEffectAction(effectResult, actualDamage, enemy.position);
            this.applyEffectResult(resolved, enemy);
          }

          if (result.isCritical) {
            const critEffects = triggerManager.processTrigger('on_crit', playerCalc, hitEventData);
            for (const effectResult of critEffects) {
              const resolved = resolveEffectAction(effectResult, actualDamage, enemy.position);
              this.applyEffectResult(resolved, enemy);
            }
          }

          const skillHitEffects = triggerManager.processTrigger('on_skill_hit', playerCalc, hitEventData);
          for (const effectResult of skillHitEffects) {
            const resolved = resolveEffectAction(effectResult, actualDamage, enemy.position);
            this.applyEffectResult(resolved, enemy);
          }

          // Apply stone on-hit effects (slow, knockback, dot)
          const stoneEffects = getStoneOnHitEffects(skill);
          if (stoneEffects.slowPercent > 0) {
            enemy.applyStatusEffect({ type: 'chill', duration: 2.0, slowPercent: stoneEffects.slowPercent }, this.player.id);
          }
          if (stoneEffects.knockbackForce > 0) {
            const kbDirX = enemy.position.x - this.player.position.x;
            const kbDirY = enemy.position.y - this.player.position.y;
            enemy.applyKnockback(kbDirX, kbDirY, stoneEffects.knockbackForce * 5);
          }
          if (stoneEffects.dot) {
            enemy.applyStatusEffect({ type: 'burn', duration: stoneEffects.dot.duration, baseDamage: stoneEffects.dot.damage }, this.player.id);
          }

          const lifeLeech = CombatSystem.calculateLeech(actualDamage, playerCalc, 'life');
          if (lifeLeech > 0) {
            this.player.heal(lifeLeech);
            getDamageNumbers().spawn(this.player.position.x, this.player.position.y - 40, lifeLeech, 'heal');
          }

          const manaLeech = CombatSystem.calculateLeech(actualDamage, playerCalc, 'mana');
          if (manaLeech > 0) {
            this.player.stats.mana = Math.min(
              this.player.stats.maxMana,
              this.player.stats.mana + manaLeech
            );
          }

          switch (element) {
            case 'od':
              this.particleSystem.emitFire(enemy.position.x, enemy.position.y - enemy.height / 2);
              break;
            case 'su':
              this.particleSystem.emitIce(enemy.position.x, enemy.position.y - enemy.height / 2);
              break;
            case 'gok':
              this.particleSystem.emitLightning(enemy.position.x, enemy.position.y - enemy.height / 2);
              break;
            case 'karanlik':
              this.particleSystem.emitMagic(enemy.position.x, enemy.position.y - enemy.height / 2, '#9b59b6');
              break;
            default:
              this.particleSystem.emitHit(enemy.position.x, enemy.position.y - enemy.height / 2);
          }

          getDamageNumbers().spawn(
            enemy.position.x,
            enemy.position.y - enemy.height,
            actualDamage,
            result.isCritical ? 'crit' : (element as DamageNumberType)
          );

          const audio = getAudioManager();
          if (result.isCritical) {
            this.screenShake.shakeHeavyHit();
            this.screenShake.triggerHitStop(0.05);
            this.screenShake.triggerFlash('#ffffff', 0.08, 0.2);
            this.particleSystem.emitBlood(enemy.position.x, enemy.position.y - enemy.height / 2);
            audio.play('hit_critical');
          } else {
            this.screenShake.shakeHit();
            this.screenShake.triggerHitStop(0.03);
            audio.play(Math.random() < 0.5 ? 'enemy_hit' : 'enemy_hit_1');
          }

          this.processLegendaryEffects('on_hit', enemy.position.x, enemy.position.y, actualDamage);

          if (!enemy.isAlive || enemy.isDying) {
            audio.play('enemy_death');
            this.screenShake.triggerFlash('#ff4444', 0.12, 0.15);
            this.screenShake.triggerHitStop(0.06);
            this.processLegendaryEffects('on_kill', enemy.position.x, enemy.position.y, actualDamage);
          }

          // V2 Projectile fork: on hit, spawn 2 new projectiles at ±30°
          if (isV2 && isSkillV2(skill) && projectileContact === 'fork' && forkCount > 0) {
            const hitX = enemy.position.x;
            const hitY = enemy.position.y;
            const origAngle = Math.atan2(direction.y, direction.x);
            const forkDamage = Math.floor(auraBoostedDamage * (0.7 / forkCount)); // damage split among forks
            const totalSpread = Math.min(Math.PI, (forkCount - 1) * (Math.PI / 6)); // spread widens with count

            for (let fi = 0; fi < forkCount; fi++) {
              const angleOff = forkCount === 1 ? 0 : totalSpread * ((fi / (forkCount - 1)) - 0.5);
              const forkAngle = origAngle + angleOff;
              const forkDir = { x: Math.cos(forkAngle), y: Math.sin(forkAngle) };

              const forkProj = new ProjectileEffect({
                skill,
                origin: { x: hitX, y: hitY },
                direction: forkDir,
                targetPosition: { x: hitX + forkDir.x * range, y: hitY + forkDir.y * range },
              });

              const forkOnHit = (forkEnemy: Enemy) => {
                if (!forkEnemy.isAlive || forkEnemy.isDying) return;
                const forkResult = CombatSystem.calculateDamageWithModifiers(
                  forkDamage, element, combatContext, this.player.level
                );
                if (!forkResult.isDodged) {
                  const forkActual = forkEnemy.takeDamage(forkResult.damage, element, forkResult.bypassES);
                  getDamageNumbers().spawn(
                    forkEnemy.position.x, forkEnemy.position.y - forkEnemy.height,
                    forkActual, forkResult.isCritical ? 'crit' : (element as DamageNumberType)
                  );
                  this.particleSystem.emitHit(forkEnemy.position.x, forkEnemy.position.y - forkEnemy.height / 2);
                }
              };

              forkProj.setCollisionData(() => this.enemies, 30, 0, forkOnHit);
              this.skillEffects.push(forkProj);
            }
          }

          // V2 Projectile chain: bounce damage to nearby enemies
          if (isV2 && isSkillV2(skill) && chainCount > 0) {
            let chainsRemaining = chainCount;
            const chainedIds = new Set<string>([enemy.id]);
            let chainSourceX = enemy.position.x;
            let chainSourceY = enemy.position.y;
            const chainDmgMult = 0.7; // Chain damage is 70% of original
            let chainDmg = Math.floor(actualDamage * chainDmgMult);

            while (chainsRemaining > 0 && chainDmg > 0) {
              // Find closest unchained enemy within 200px
              let closest: Enemy | null = null;
              let closestDistSq = 200 * 200;
              for (const e of this.enemies) {
                if (!e.isAlive || e.isDying || chainedIds.has(e.id)) continue;
                const cdx = e.position.x - chainSourceX;
                const cdy = e.position.y - chainSourceY;
                const dSq = cdx * cdx + cdy * cdy;
                if (dSq < closestDistSq) {
                  closestDistSq = dSq;
                  closest = e;
                }
              }
              if (!closest) break;

              chainedIds.add(closest.id);
              // Chain lightning line from source to target
              this.particleSystem.emitChainLine(chainSourceX, chainSourceY, closest.position.x, closest.position.y, ELEMENT_CHAIN_COLORS[element] || '#88ddff');
              const chainActual = closest.takeDamage(chainDmg, element);
              if (chainActual > 0) {
                getDamageNumbers().spawn(closest.position.x, closest.position.y - closest.height, chainActual, element as DamageNumberType);
                this.particleSystem.emitLightning(closest.position.x, closest.position.y);
              }
              chainSourceX = closest.position.x;
              chainSourceY = closest.position.y;
              chainDmg = Math.floor(chainDmg * chainDmgMult);
              chainsRemaining--;
            }
          }
        } else {
          this.particleSystem.addMissText(enemy.position.x, enemy.position.y - enemy.height);
          getDamageNumbers().spawn(enemy.position.x, enemy.position.y - enemy.height, 0, 'miss');
        }
      };

      // Get V2 chain count (only active when projectileContact is 'chain')
      let chainCount = 0;
      if (isV2 && isSkillV2(skill) && projectileContact === 'chain') {
        chainCount = SkillCreatorV2.getChainCount(skill);
      }

      // Create projectile(s) with collision
      if (projectileCount > 1) {
        // Multishot: create multiple projectiles in a fan spread
        const spreadAngle = Math.PI / 6; // 30 degrees total spread
        const baseAngle = Math.atan2(direction.y, direction.x);
        for (let i = 0; i < projectileCount; i++) {
          const angleOffset = spreadAngle * ((i / (projectileCount - 1)) - 0.5);
          const projAngle = baseAngle + angleOffset;
          const projDir = { x: Math.cos(projAngle), y: Math.sin(projAngle) };

          const projEffect = new ProjectileEffect({
            skill,
            origin: { ...this.player.position },
            direction: projDir,
            targetPosition: targetPos,
          });
          projEffect.setCollisionData(() => this.enemies, 30, pierceCount, onHitEnemy);
          this.skillEffects.push(projEffect);
        }
      } else {
        // Single projectile
        const projEffect = new ProjectileEffect({
          skill,
          origin: { ...this.player.position },
          direction,
          targetPosition: targetPos,
        });
        projEffect.setCollisionData(() => this.enemies, 30, pierceCount, onHitEnemy);
        this.skillEffects.push(projEffect);
      }

      // Projectile damage is handled by collision callback - skip the damage loop below
      return;
    }

    // Determine effective target position for AOE (respect aoePosition choice)
    let effectiveTargetPos = targetPos;
    if (isV2 && isSkillV2(skill) && skill.deliveryType === 'aoe') {
      const aoePosition = skill.aoePosition || 'target';
      if (aoePosition === 'self') {
        effectiveTargetPos = { x: this.player.position.x, y: this.player.position.y };
      }
      // Check for AOE leap shape: move player to target then AOE at landing spot
      const aoeShape = SkillCreatorV2.getAOEShape(skill);
      if (aoeShape === 'leap') {
        const leapX = targetPos.x;
        const leapY = targetPos.y;
        this.player.position.x = leapX;
        this.player.position.y = leapY;
        effectiveTargetPos = { x: leapX, y: leapY };
      }
    }

    // Create visual effect for non-projectile skills
    const effect = createSkillEffect({
      skill,
      origin: { ...this.player.position },
      direction,
      targetPosition: effectiveTargetPos,
    });
    // Persistent AOE: match visual effect duration to skill duration
    if (isV2 && isSkillV2(skill) && skill.deliveryType === 'aoe' && (skill.aoeTrigger || 'instant') === 'persistent') {
      effect.setDuration(SkillCreatorV2.getAOEDuration(skill));
    }
    this.skillEffects.push(effect);

    if (AOE_SKILL_TYPES.has(baseType)) {
      // Check for V2 AOE trigger type
      const aoeTrigger = (isV2 && isSkillV2(skill)) ? (skill.aoeTrigger || 'instant') : 'instant';

      // Check if this is a V2 AOE skill with cone shape
      if (isV2 && isSkillV2(skill) && skill.deliveryType === 'aoe') {
        const aoeShape = SkillCreatorV2.getAOEShape(skill);
        if (aoeShape === 'cone') {
          // Cone AOE - originates from player position
          const coneAngle = SkillCreatorV2.getAOEConeAngle(skill) * (Math.PI / 180);
          const coneLength = SkillCreatorV2.getAOEConeLength(skill);
          hitEnemies = CombatSystem.getEntitiesInCone(
            this.enemies,
            this.player.position.x,
            this.player.position.y,
            direction.x,
            direction.y,
            coneLength,
            coneAngle
          );
        } else if (aoeShape === 'line') {
          // Line AOE - rectangle from player toward target
          const lineWidth = areaRadius > 0 ? areaRadius : 40;
          const lineLength = range > 0 ? range : 150;
          // Hit enemies in a rectangular area along the direction
          const hitSet = new Set<string>();
          const steps = Math.max(3, Math.floor(lineLength / 30));
          for (let s = 0; s <= steps; s++) {
            const t = s / steps;
            const px = this.player.position.x + direction.x * lineLength * t;
            const py = this.player.position.y + direction.y * lineLength * t;
            const nearby = CombatSystem.getEntitiesInRadius(this.enemies, px, py, lineWidth / 2);
            for (const e of nearby) {
              if (!hitSet.has(e.id)) {
                hitSet.add(e.id);
                hitEnemies.push(e);
              }
            }
          }
        } else {
          // Circle AOE - at effective target position (respects aoePosition)
          hitEnemies = CombatSystem.getEntitiesInRadius(
            this.enemies,
            effectiveTargetPos.x,
            effectiveTargetPos.y,
            areaRadius > 0 ? areaRadius : 60
          );
        }
      } else {
        // Legacy: AoE at target position (or around player for spin/shockwave)
        const selfCentered = ['spin', 'shockwave'].includes(baseType);
        const centerX = selfCentered ? this.player.position.x : effectiveTargetPos.x;
        const centerY = selfCentered ? this.player.position.y : effectiveTargetPos.y;
        hitEnemies = CombatSystem.getEntitiesInRadius(
          this.enemies,
          centerX,
          centerY,
          areaRadius > 0 ? areaRadius : 60
        );
      }

      // Handle V2 AOE trigger modes (persistent, delayed)
      if (isV2 && isSkillV2(skill) && aoeTrigger !== 'instant') {
        if (aoeTrigger === 'delayed') {
          // Delayed: schedule damage after delay
          const delay = SkillCreatorV2.getAOEDelay(skill);
          const delayedEnemyGetter = () => CombatSystem.getEntitiesInRadius(
            this.enemies,
            effectiveTargetPos.x,
            effectiveTargetPos.y,
            areaRadius > 0 ? areaRadius : 60
          );
          setTimeout(() => {
            const delayedHitEnemies = delayedEnemyGetter();
            this.applyAOEDamageToEnemies(delayedHitEnemies, damage, element, skill, baseType);
            this.particleSystem.emitExplosion(effectiveTargetPos.x, effectiveTargetPos.y, areaRadius || 60);
            this.screenShake.shakeExplosion();
          }, delay * 1000);
          // Don't apply damage immediately - return before the damage loop
          return;
        } else if (aoeTrigger === 'persistent') {
          // Persistent: create lingering damage zone that ticks every 0.5s
          const duration = SkillCreatorV2.getAOEDuration(skill);
          const tickInterval = 0.5;
          const totalTicks = Math.floor(duration / tickInterval);
          const tickDamage = Math.floor(damage / Math.max(1, totalTicks));
          let ticksRemaining = totalTicks;
          const persistentInterval = setInterval(() => {
            ticksRemaining--;
            if (ticksRemaining <= 0 || !this.player.isAlive) {
              clearInterval(persistentInterval);
              return;
            }
            const tickHitEnemies = CombatSystem.getEntitiesInRadius(
              this.enemies,
              effectiveTargetPos.x,
              effectiveTargetPos.y,
              areaRadius > 0 ? areaRadius : 60
            );
            this.applyAOEDamageToEnemies(tickHitEnemies, tickDamage, element, skill, baseType);
          }, tickInterval * 1000);
          // Don't apply initial damage for persistent - it ticks over time
          return;
        }
      }
    } else if (MELEE_SKILL_TYPES.has(baseType)) {
      // Check for V2 melee thrust (dash forward)
      const meleeType = (isV2 && isSkillV2(skill)) ? (skill.meleeType || 'strike') : 'strike';
      if (meleeType === 'thrust') {
        // Thrust: animated dash forward, hit enemies along path
        const v2Skill = isV2 && isSkillV2(skill) ? skill : null;
        const thrustDist = v2Skill ? (v2Skill.sliders as any).thrustRange || range : range;
        // Apply thrust damage multiplier (more range = less damage)
        if (v2Skill && v2Skill._thrustDamageMult) {
          damage = Math.floor(damage * v2Skill._thrustDamageMult);
        }
        const startX = this.player.position.x;
        const startY = this.player.position.y;
        const endX = startX + direction.x * thrustDist;
        const endY = startY + direction.y * thrustDist;

        // Collect enemies along thrust line (damage applied immediately but player dashes over time)
        const hitSet = new Set<string>();
        const steps = Math.max(3, Math.floor(thrustDist / 30));
        for (let s = 0; s <= steps; s++) {
          const t = s / steps;
          const px = startX + (endX - startX) * t;
          const py = startY + (endY - startY) * t;
          const nearby = CombatSystem.getEntitiesInRadius(this.enemies, px, py, 40);
          for (const e of nearby) {
            if (!hitSet.has(e.id)) {
              hitSet.add(e.id);
              hitEnemies.push(e);
            }
          }
        }

        // Start animated dash (player moves over attackCooldown duration)
        this._thrustDash = {
          startX, startY, endX, endY,
          duration: this.player.attackCooldown,
          elapsed: 0,
        };
      } else {
        // Strike (default): lock onto closest enemy in range in facing direction
        let closestEnemy: Enemy | null = null;
        let closestDist = Infinity;
        const px = this.player.position.x;
        const py = this.player.position.y;

        for (const enemy of this.enemies) {
          if (!enemy.isAlive || enemy.isDying) continue;
          const dx = enemy.position.x - px;
          const dy = enemy.position.y - py;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > range * 1.5) continue; // Out of melee range

          // Check enemy is roughly in facing direction (within 90 degrees)
          const enemyAngle = Math.atan2(dy, dx);
          const facingAngle = Math.atan2(direction.y, direction.x);
          let angleDiff = Math.abs(enemyAngle - facingAngle);
          if (angleDiff > Math.PI) angleDiff = 2 * Math.PI - angleDiff;
          if (angleDiff > Math.PI / 2) continue; // Behind player

          if (dist < closestDist) {
            closestDist = dist;
            closestEnemy = enemy;
          }
        }

        if (closestEnemy) {
          hitEnemies = [closestEnemy];
        }
      }
    } else if (INSTANT_SKILL_TYPES.has(baseType)) {
      // Rain, beam, chain - instant damage (not collision-based)
      const attackX = this.player.position.x + direction.x * range / 2;
      const attackY = this.player.position.y + direction.y * range / 2;
      hitEnemies = CombatSystem.getEntitiesInRadius(
        this.enemies,
        attackX,
        attackY,
        range / 2
      );
    } else {
      // Default: melee behavior
      const attackX = this.player.position.x + direction.x * (range / 2);
      const attackY = this.player.position.y + direction.y * (range / 2);
      hitEnemies = CombatSystem.getEntitiesInRadius(
        this.enemies,
        attackX,
        attackY,
        areaRadius > 0 ? areaRadius : range
      );
    }

    // Get player's stat calculator for modifier-based damage
    const playerCalc = this.player.getStatCalculator();
    const triggerManager = getTriggerEffectManager();

    // Apply aura damage bonus
    const auraBonus = this.player.getAuraDamageBonus(element);
    const auraBoostedDamage = Math.floor(damage * (1 + auraBonus));

    // Create combat context
    const combatContext: CombatContext = {
      attackerCalculator: playerCalc,
      isSpell: true,
      // Note: enemies don't have StatCalculator yet, so use base stats
    };

    // Apply damage to hit enemies
    for (const enemy of hitEnemies) {
      if (!enemy.isAlive || enemy.isDying) continue;

      // Use enhanced damage calculation with modifiers (includes aura bonus)
      const result = CombatSystem.calculateDamageWithModifiers(
        auraBoostedDamage,
        element,
        combatContext,
        this.player.level
      );

      if (!result.isDodged) {
        const actualDamage = enemy.takeDamage(result.damage, element, result.bypassES);

        // Apply ailment from combat result
        if (result.ailment && result.ailment.applied) {
          enemy.applyStatusEffect({
            type: result.ailment.type,
            duration: STATUS_DURATIONS[result.ailment.type] || 3,
            baseDamage: result.ailment.damage,
          }, this.player.id);
        }

        // Process on_hit trigger effects
        const hitEventData: TriggerEventData = {
          sourceEntityId: this.player.id,
          targetEntityId: enemy.id,
          damageDealt: actualDamage,
          damageType: element,
          isCritical: result.isCritical,
          positionX: enemy.position.x,
          positionY: enemy.position.y,
          skillId: skill.id,
          skillElement: element,
          skillBaseType: baseType,
          attackerHpPercent: this.player.getHealthPercent() * 100,
        };

        // Process on_hit effects
        const hitEffects = triggerManager.processTrigger('on_hit', playerCalc, hitEventData);
        for (const effectResult of hitEffects) {
          const resolved = resolveEffectAction(effectResult, actualDamage, enemy.position);
          this.applyEffectResult(resolved, enemy);
        }

        // Process on_crit effects
        if (result.isCritical) {
          const critEffects = triggerManager.processTrigger('on_crit', playerCalc, hitEventData);
          for (const effectResult of critEffects) {
            const resolved = resolveEffectAction(effectResult, actualDamage, enemy.position);
            this.applyEffectResult(resolved, enemy);
          }
        }

        // Process on_skill_hit effects
        const skillHitEffects = triggerManager.processTrigger('on_skill_hit', playerCalc, hitEventData);
        for (const effectResult of skillHitEffects) {
          const resolved = resolveEffectAction(effectResult, actualDamage, enemy.position);
          this.applyEffectResult(resolved, enemy);
        }

        // Apply stone on-hit effects (slow, knockback, dot)
        const stoneEffects = getStoneOnHitEffects(skill);
        if (stoneEffects.slowPercent > 0) {
          enemy.applyStatusEffect({ type: 'chill', duration: 2.0, slowPercent: stoneEffects.slowPercent }, this.player.id);
        }
        if (stoneEffects.knockbackForce > 0) {
          const kbDirX = enemy.position.x - this.player.position.x;
          const kbDirY = enemy.position.y - this.player.position.y;
          enemy.applyKnockback(kbDirX, kbDirY, stoneEffects.knockbackForce * 5);
        }
        if (stoneEffects.dot) {
          enemy.applyStatusEffect({ type: 'burn', duration: stoneEffects.dot.duration, baseDamage: stoneEffects.dot.damage }, this.player.id);
        }

        // Calculate life leech from modifiers
        const lifeLeech = CombatSystem.calculateLeech(actualDamage, playerCalc, 'life');
        if (lifeLeech > 0) {
          this.player.heal(lifeLeech);
          getDamageNumbers().spawn(this.player.position.x, this.player.position.y - 40, lifeLeech, 'heal');
        }

        const manaLeech = CombatSystem.calculateLeech(actualDamage, playerCalc, 'mana');
        if (manaLeech > 0) {
          this.player.stats.mana = Math.min(
            this.player.getEffectiveMaxMana(),
            this.player.stats.mana + manaLeech
          );
        }

        // Element-based particle effects (Turkish mythology elements)
        switch (element) {
          case 'od': // Fire
            this.particleSystem.emitFire(enemy.position.x, enemy.position.y - enemy.height / 2);
            break;
          case 'su': // Water/Ice
            this.particleSystem.emitIce(enemy.position.x, enemy.position.y - enemy.height / 2);
            break;
          case 'gok': // Sky/Lightning
            this.particleSystem.emitLightning(enemy.position.x, enemy.position.y - enemy.height / 2);
            break;
          case 'karanlik': // Dark
            this.particleSystem.emitMagic(enemy.position.x, enemy.position.y - enemy.height / 2, '#9b59b6');
            break;
          default:
            this.particleSystem.emitHit(enemy.position.x, enemy.position.y - enemy.height / 2);
        }

        // Damage number
        getDamageNumbers().spawn(
          enemy.position.x,
          enemy.position.y - enemy.height,
          actualDamage,
          result.isCritical ? 'crit' : (element as DamageNumberType)
        );

        // Screen shake + sound for skills
        const audio = getAudioManager();
        if (result.isCritical) {
          this.screenShake.shakeHeavyHit();
          this.screenShake.triggerHitStop(0.05);
          this.screenShake.triggerFlash('#ffffff', 0.08, 0.2);
          audio.play('hit_critical');
        } else {
          this.screenShake.shakeHit();
          this.screenShake.triggerHitStop(0.03);
          audio.play(Math.random() < 0.5 ? 'enemy_hit' : 'enemy_hit_1');
        }

        // Process legendary on_hit effects for skill attacks
        this.processLegendaryEffects('on_hit', enemy.position.x, enemy.position.y, actualDamage);

        // Enemy death sound + on_kill triggers
        if (!enemy.isAlive || enemy.isDying) {
          audio.play('enemy_death');
          this.screenShake.triggerFlash('#ff4444', 0.12, 0.15);
          this.screenShake.triggerHitStop(0.06);
          this.processLegendaryEffects('on_kill', enemy.position.x, enemy.position.y, actualDamage);
        }
      } else {
        this.particleSystem.addMissText(enemy.position.x, enemy.position.y - enemy.height);
        getDamageNumbers().spawn(enemy.position.x, enemy.position.y - enemy.height, 0, 'miss');
      }
    }
  }

  /**
   * Helper: apply AOE damage to a list of enemies (used by persistent/delayed AOE triggers)
   */
  private applyAOEDamageToEnemies(
    enemies: Enemy[],
    baseDamage: number,
    element: import('@shared/types').ElementType,
    skill: HotbarSkill,
    baseType: string
  ): void {
    const playerCalc = this.player.getStatCalculator();
    const triggerManager = getTriggerEffectManager();
    const auraBonus = this.player.getAuraDamageBonus(element);
    const auraBoostedDamage = Math.floor(baseDamage * (1 + auraBonus));
    const combatContext: CombatContext = { attackerCalculator: playerCalc, isSpell: true };

    for (const enemy of enemies) {
      if (!enemy.isAlive || enemy.isDying) continue;
      const result = CombatSystem.calculateDamageWithModifiers(
        auraBoostedDamage, element, combatContext, this.player.level
      );
      if (!result.isDodged) {
        const actualDamage = enemy.takeDamage(result.damage, element, result.bypassES);

        // Trigger effects
        const hitEventData: TriggerEventData = {
          sourceEntityId: this.player.id,
          targetEntityId: enemy.id,
          damageDealt: actualDamage,
          damageType: element,
          isCritical: result.isCritical,
          positionX: enemy.position.x,
          positionY: enemy.position.y,
          skillId: skill.id,
          skillElement: element,
          skillBaseType: baseType,
          attackerHpPercent: this.player.getHealthPercent() * 100,
        };
        const hitEffects = triggerManager.processTrigger('on_hit', playerCalc, hitEventData);
        for (const effectResult of hitEffects) {
          const resolved = resolveEffectAction(effectResult, actualDamage, enemy.position);
          this.applyEffectResult(resolved, enemy);
        }

        getDamageNumbers().spawn(
          enemy.position.x,
          enemy.position.y - enemy.height,
          actualDamage,
          result.isCritical ? 'crit' : (element as DamageNumberType)
        );
        if (result.isCritical) {
          this.screenShake.shakeHeavyHit();
        } else {
          this.screenShake.shakeHit();
        }
      }
    }
  }

  /**
   * Process legendary item effects based on trigger type
   */
  private processLegendaryEffects(trigger: string, enemyX: number, enemyY: number, damage: number): void {
    const allEquipped = this.player.inventory.getAllEquipped();

    for (const [, item] of allEquipped) {
      if (!item || item.rarity !== 'legendary') continue;

      // Check if this item has legendary effects (LegendaryItem extends EquipmentItem)
      const legendaryItem = item as unknown as LegendaryItem;
      if (!legendaryItem.legendaryEffects || legendaryItem.legendaryEffects.length === 0) continue;

      for (const effect of legendaryItem.legendaryEffects) {
        if (effect.type !== trigger) continue;

        // Check chance
        if (effect.chance !== undefined && Math.random() * 100 > effect.chance) continue;

        // Check cooldown
        const cooldownKey = `${legendaryItem.uniqueId || item.id}_${effect.effectId}`;
        const now = Date.now() / 1000;
        const lastTrigger = this.legendaryEffectCooldowns.get(cooldownKey) || 0;
        if (effect.cooldown && now - lastTrigger < effect.cooldown) continue;

        this.legendaryEffectCooldowns.set(cooldownKey, now);

        // Execute effect
        this.executeLegendaryEffect(effect, enemyX, enemyY, damage);
      }
    }
  }

  /**
   * Execute a specific legendary item effect
   */
  private executeLegendaryEffect(effect: LegendaryEffect, x: number, y: number, _damage: number): void {
    switch (effect.effectId) {
      // === Damage Nova effects ===
      case 'wind_strike':
      case 'flame_touch':
      case 'fire_seal_burn':
      case 'frost_nova_ring':
      case 'dragon_breath':
      case 'fire_seal_explosion':
      case 'wolf_fang': {
        // Damage all enemies in radius
        const radius = 100;
        const radiusSq = radius * radius;
        for (const enemy of this.enemies) {
          if (!enemy.isAlive || enemy.isDying) continue;
          const dx = enemy.position.x - x;
          const dy = enemy.position.y - y;
          if (dx * dx + dy * dy < radiusSq) {
            const novaDamage = effect.value || 50;
            enemy.takeDamage(novaDamage);
            getDamageNumbers().spawn(enemy.position.x, enemy.position.y - 30, novaDamage, 'physical');
          }
        }
        this.particleSystem.emitExplosion(x, y, radius);
        break;
      }

      // === Healing effects ===
      case 'battle_frenzy':
      case 'soul_drain': {
        const healAmount = Math.floor(_damage * (effect.value || 5) / 100);
        if (healAmount > 0) {
          this.player.heal(healAmount);
          this.particleSystem.addHealNumber(this.player.position.x, this.player.position.y - 30, healAmount);
          getDamageNumbers().spawn(this.player.position.x, this.player.position.y - 30, healAmount, 'heal');
        }
        break;
      }

      // === Speed/Buff effects ===
      case 'wolf_howl':
      case 'warrior_fury':
      case 'predator_instinct':
      case 'quick_escape':
      case 'shadow_dash':
      case 'shadow_cloak': {
        // Grant brief invincibility as a simple buff representation
        const duration = effect.duration || 1;
        this.player.setInvincible(duration);
        break;
      }

      // === Chain Lightning ===
      case 'chain_lightning_gloves':
      case 'thunder_strike':
      case 'divine_wrath': {
        let chainCount = 0;
        const maxChains = 3;
        const chainRadiusSq = 200 * 200;
        for (const enemy of this.enemies) {
          if (!enemy.isAlive || enemy.isDying || chainCount >= maxChains) continue;
          const dx = enemy.position.x - x;
          const dy = enemy.position.y - y;
          if (dx * dx + dy * dy < chainRadiusSq) {
            const chainDmg = effect.value || 30;
            enemy.takeDamage(chainDmg);
            getDamageNumbers().spawn(enemy.position.x, enemy.position.y - 30, chainDmg, 'gok');
            this.particleSystem.emitLightning(enemy.position.x, enemy.position.y);
            chainCount++;
          }
        }
        break;
      }

      // === Wolf/Summon effects (damage over time to nearby enemies) ===
      case 'wolf_spirit':
      case 'raven_fire': {
        // Instant burst to nearby enemies as a simplified summon
        const burstRadiusSq = 150 * 150;
        for (const enemy of this.enemies) {
          if (!enemy.isAlive || enemy.isDying) continue;
          const dx = enemy.position.x - x;
          const dy = enemy.position.y - y;
          if (dx * dx + dy * dy < burstRadiusSq) {
            const dmg = effect.value || 30;
            enemy.takeDamage(dmg);
            getDamageNumbers().spawn(enemy.position.x, enemy.position.y - 30, dmg, 'physical');
          }
        }
        this.particleSystem.emitExplosion(x, y, 80);
        break;
      }

      // === Fear/CC effects ===
      case 'wolf_howl_helm': {
        // Apply brief slow/stun to nearby enemies (simplified as damage)
        const fearRadiusSq = 120 * 120;
        for (const enemy of this.enemies) {
          if (!enemy.isAlive || enemy.isDying) continue;
          const dx = enemy.position.x - x;
          const dy = enemy.position.y - y;
          if (dx * dx + dy * dy < fearRadiusSq) {
            // Small damage + visual effect for fear
            enemy.takeDamage(5);
            this.particleSystem.emitMagic(enemy.position.x, enemy.position.y, '#8844aa');
          }
        }
        break;
      }

      // === Reflect/Shield effects ===
      case 'iron_will':
      case 'iron_skin_reflect':
      case 'divine_shield':
      case 'holy_shield':
      case 'divine_intervention': {
        // Grant brief invincibility
        this.player.setInvincible(effect.duration || 0.5);
        this.particleSystem.emitMagic(this.player.position.x, this.player.position.y, '#ffd700');
        break;
      }

      // === Burn/DoT effects ===
      case 'burning_touch':
      case 'frost_touch': {
        // Instant damage as simplified DoT
        const dotDmg = effect.value || 15;
        const dotRadiusSq = 50 * 50;
        // Find the nearest enemy at position
        for (const enemy of this.enemies) {
          if (!enemy.isAlive || enemy.isDying) continue;
          const dx = enemy.position.x - x;
          const dy = enemy.position.y - y;
          if (dx * dx + dy * dy < dotRadiusSq) {
            enemy.takeDamage(dotDmg);
            getDamageNumbers().spawn(enemy.position.x, enemy.position.y - 30, dotDmg, 'burn_tick');
            break;
          }
        }
        break;
      }

      // === Default: generic damage at position ===
      default: {
        // For any unhandled effect, deal value as damage to nearest enemy
        if (effect.value > 0) {
          const defaultRadiusSq = 80 * 80;
          for (const enemy of this.enemies) {
            if (!enemy.isAlive || enemy.isDying) continue;
            const dx = enemy.position.x - x;
            const dy = enemy.position.y - y;
            if (dx * dx + dy * dy < defaultRadiusSq) {
              enemy.takeDamage(effect.value);
              getDamageNumbers().spawn(enemy.position.x, enemy.position.y - 30, effect.value, 'physical');
              break;
            }
          }
        }
        break;
      }
    }
  }

  /**
   * Apply resolved effect result to game state
   */
  private applyEffectResult(result: ReturnType<typeof resolveEffectAction>, targetEnemy?: Enemy): void {
    if (result.healAmount && result.healAmount > 0) {
      this.player.heal(result.healAmount);
      // Visual feedback for healing
      this.particleSystem.addHealNumber(
        this.player.position.x,
        this.player.position.y - 40,
        result.healAmount
      );
      getDamageNumbers().spawn(this.player.position.x, this.player.position.y - 40, result.healAmount, 'heal');
    }

    if (result.manaAmount && result.manaAmount > 0) {
      this.player.stats.mana = Math.min(
        this.player.stats.maxMana,
        this.player.stats.mana + Math.floor(result.manaAmount)
      );
      // Visual feedback for mana restore
      this.particleSystem.addManaNumber(
        this.player.position.x,
        this.player.position.y - 30,
        result.manaAmount
      );
    }

    if (result.damageToApply) {
      const { amount, element, aoe, x, y } = result.damageToApply;
      const radius = aoe || 50;

      // Find enemies in area and deal damage
      const targets = CombatSystem.getEntitiesInRadius(this.enemies, x, y, radius);
      for (const target of targets) {
        if (target.isAlive) {
          target.takeDamage(amount, element as import('@shared/types').ElementType);
          this.particleSystem.emitExplosion(target.position.x, target.position.y, radius / 2);
        }
      }
    }

    // Apply status effects
    if (result.statusToApply && targetEnemy) {
      const { status, duration, stacks } = result.statusToApply;
      targetEnemy.applyStatusEffect(
        {
          type: status as import('@shared/effects/StatusEffect').StatusType,
          duration,
          stacks,
        },
        this.player.id
      );
    }

    // Remove status effects
    if (result.statusToRemove) {
      this.player.removeStatusEffect(result.statusToRemove as import('@shared/effects/StatusEffect').StatusType);
    }

    // Spawn visual effect
    if (result.spawnEffect) {
      const pos = targetEnemy
        ? { x: targetEnemy.position.x, y: targetEnemy.position.y }
        : { x: this.player.position.x, y: this.player.position.y };

      switch (result.spawnEffect) {
        case 'explosion':
          this.particleSystem.emitExplosion(pos.x, pos.y, 60);
          break;
        case 'fire':
          this.particleSystem.emitFire(pos.x, pos.y);
          break;
        case 'ice':
          this.particleSystem.emitIce(pos.x, pos.y);
          break;
        case 'lightning':
          this.particleSystem.emitLightning(pos.x, pos.y);
          break;
        case 'magic':
          this.particleSystem.emitMagic(pos.x, pos.y, '#aa88ff');
          break;
        case 'blood':
          this.particleSystem.emitBlood(pos.x, pos.y);
          break;
        case 'heal':
          this.particleSystem.emitMagic(pos.x, pos.y, '#44ff44');
          break;
        case 'levelup':
          this.particleSystem.emitLevelUp(pos.x, pos.y);
          break;
        default:
          this.particleSystem.emitHit(pos.x, pos.y);
          break;
      }
    }
  }

  render(renderer: Renderer): void {
    const ctx = renderer.context;
    const width = renderer.width;

    // Set shared frame time once for all entities (avoids per-entity Date.now() calls)
    const frameTimeSec = performance.now() / 1000;
    setEnemyFrameTime(frameTimeSec);
    DroppedItem.setFrameTime(performance.now());
    this._frameTime = frameTimeSec;

    // Render map
    this.map.render(renderer, this.camera);

    // Render map props (trees, rocks, etc.) - pass player position for occlusion transparency
    this.map.renderProps(renderer, this.camera, this.player.position);

    // Dynamic torch/campfire lighting
    this.renderTorchLighting(renderer);

    // Render dropped items (below entities) — viewport culling
    for (const item of this.droppedItems) {
      if (this.camera.isVisible(item.position.x, item.position.y, 60)) {
        item.render(renderer, this.camera);
      }
    }

    // Render V2 aura effects (below entities, on the ground) — viewport culling
    for (const aura of this.auraEffects) {
      if (this.camera.isVisible(aura.position.x, aura.position.y, 300)) {
        aura.render(renderer, this.camera);
      }
    }

    // Render boss attack telegraphs (ground warning indicators)
    this.renderBossTelegraphs(renderer.context, this.camera);

    // Collect visible entities for depth sorting (viewport culling)
    // Reuse pre-allocated array to avoid per-frame allocation
    const entities = this.renderEntities;
    let entityCount = 0;
    const entityMargin = 80; // pixels margin for entities partially on screen

    // Add player (always visible)
    if (entityCount < entities.length) {
      entities[entityCount].entity = this.player;
      entities[entityCount].y = this.player.position.y;
    } else {
      entities.push({ entity: this.player, y: this.player.position.y });
    }
    entityCount++;

    // Add enemies (only visible ones)
    for (const enemy of this.enemies) {
      if ((enemy.isAlive || enemy.isDying) && this.camera.isVisible(enemy.position.x, enemy.position.y, entityMargin)) {
        if (entityCount < entities.length) {
          entities[entityCount].entity = enemy;
          entities[entityCount].y = enemy.position.y;
        } else {
          entities.push({ entity: enemy, y: enemy.position.y });
        }
        entityCount++;
      }
    }

    // Add NPCs (only visible ones)
    for (const npc of this.npcs) {
      if (this.camera.isVisible(npc.position.x, npc.position.y, entityMargin)) {
        if (entityCount < entities.length) {
          entities[entityCount].entity = npc;
          entities[entityCount].y = npc.position.y;
        } else {
          entities.push({ entity: npc, y: npc.position.y });
        }
        entityCount++;
      }
    }

    // Sort only the active portion in-place by Y position (back to front for isometric)
    // Insertion sort: optimal for small N (typically <50 entities) and nearly-sorted data
    for (let i = 1; i < entityCount; i++) {
      const tmp = entities[i];
      let j = i - 1;
      while (j >= 0 && entities[j].y > tmp.y) {
        entities[j + 1] = entities[j];
        j--;
      }
      entities[j + 1] = tmp;
    }

    // Render entities
    for (let ei = 0; ei < entityCount; ei++) {
      const { entity } = entities[ei];
      // Apply death animation transforms for dying enemies
      if (entity instanceof Enemy && entity.isDying) {
        const ctx = renderer.context;
        entity.applyDeathTransform(ctx, entity.getScreenPosition(this.camera));
        entity.render(renderer, this.camera);
        entity.restoreDeathTransform(ctx);
      } else {
        entity.render(renderer, this.camera);
      }
    }

    // Render skill effects — viewport culling
    for (const effect of this.skillEffects) {
      const pos = effect.getWorldPosition();
      if (this.camera.isVisible(pos.x, pos.y, 300)) {
        effect.render(renderer, this.camera);
      }
    }

    // Render V2 traps — viewport culling
    for (const trap of this.traps) {
      if (this.camera.isVisible(trap.position.x, trap.position.y, 80)) {
        trap.render(renderer, this.camera);
      }
    }

    // Render V2 totems — viewport culling
    for (const totem of this.totems) {
      if (this.camera.isVisible(totem.position.x, totem.position.y, 80)) {
        totem.render(renderer, this.camera);
      }
    }

    // Render minions — viewport culling (respawning ghosts use owner position)
    for (const minion of this.minions) {
      if (minion.isAlive || minion.isDying) {
        if (this.camera.isVisible(minion.position.x, minion.position.y, 80)) {
          minion.render(renderer, this.camera);
        }
      } else if (minion.isRespawning()) {
        // Ghost renders at owner (player) position
        if (this.camera.isVisible(this.player.position.x, this.player.position.y, 80)) {
          minion.render(renderer, this.camera);
        }
      }
    }

    // Render V2 beam effects (on top) — viewport culling (bounding box of origin+endpoint)
    for (const beam of this.beamEffects) {
      const ep = beam.getEndpoint();
      const minX = Math.min(beam.origin.x, ep.x);
      const minY = Math.min(beam.origin.y, ep.y);
      const maxX = Math.max(beam.origin.x, ep.x);
      const maxY = Math.max(beam.origin.y, ep.y);
      if (this.camera.isRectVisible(minX, minY, maxX - minX, maxY - minY, 100)) {
        beam.render(renderer, this.camera);
      }
    }

    // Render particles (world space)
    this.particleSystem.render(renderer, this.camera);

    // Render weather effects (rain, snow, fog, day/night overlay)
    this.weatherSystem.render(ctx, renderer.width, renderer.height, this.camera.position.x, this.camera.position.y);

    // Render floating damage numbers (world space, after particles, before HUD)
    getDamageNumbers().render(ctx, this.camera);

    // Render waypoint
    if (this.waypoint) {
      this.waypoint.render(ctx, this.camera.position.x, this.camera.position.y);

      // Show interaction hint when player is near
      if (this.waypoint.isPlayerNear(this.player.position.x, this.player.position.y)) {
        const wpPos = this.waypoint.getPosition();
        const screenPos = this.camera.worldToScreen(wpPos.x, wpPos.y);
        const pulse = Math.sin(this._frameTime * 5) * 0.2 + 0.8;
        ctx.font = 'bold 14px Arial';
        ctx.fillStyle = `rgba(100, 180, 255, ${pulse})`;
        ctx.textAlign = 'center';
        if (this.waypoint.isActivated()) {
          ctx.fillText(t('waypoint.openHint'), screenPos.x, screenPos.y - 55);
        } else {
          ctx.fillText(t('waypoint.activateHint'), screenPos.x, screenPos.y - 55);
        }
      }
    }

    // Render zone exits/portals
    this.renderZoneExits(ctx);

    const { width: screenW, height: screenH } = ctx.canvas;

    // Render boss cinematic overlay (letterbox + name)
    if (this.bossCinematic.active) {
      this.renderBossCinematic(ctx, screenW, screenH);
    }

    // Render zone name (top of screen + animated overlay)
    this.renderZoneName(ctx, width, renderer.height);
  }

  /** Rebuild cached torch world positions (called on zone change) */
  private rebuildTorchCache(): void {
    this.cachedTorchWorldPositions = [];
    const props = this.map.getProps();
    if (props) {
      for (const prop of props) {
        if (prop.type === 'torch' || prop.type === 'campfire') {
          this.cachedTorchWorldPositions.push(this.map.tileToWorld(prop.x, prop.y));
        }
      }
    }
  }

  private renderTorchLighting(renderer: Renderer): void {
    if (this.cachedTorchWorldPositions.length === 0) return;

    // Reuse pre-allocated array to avoid per-frame allocation
    const visibleTorches = this._visibleTorches;
    let count = 0;
    for (const pos of this.cachedTorchWorldPositions) {
      if (this.camera.isVisible(pos.x, pos.y, 200)) {
        if (count < visibleTorches.length) {
          visibleTorches[count] = pos;
        } else {
          visibleTorches.push(pos);
        }
        count++;
      }
    }
    visibleTorches.length = count;
    if (count > 0) {
      renderer.renderTorchLighting(visibleTorches, this.camera);
    }
  }

  private renderZoneExits(ctx: CanvasRenderingContext2D): void {
    const exits = this.zoneManager.getExits();

    for (const exit of exits) {
      const screenPos = this.camera.worldToScreen(exit.position.x, exit.position.y);

      // Check if exit is on screen
      const { width, height } = ctx.canvas;
      if (screenPos.x < -50 || screenPos.x > width + 50 ||
          screenPos.y < -50 || screenPos.y > height + 50) {
        continue;
      }

      ctx.save();

      // Draw portal/path indicator
      if (exit.portalType === 'portal') {
        // Magical portal
        const pulse = Math.sin(this._frameTime * 1000 / 300) * 0.3 + 0.7;
        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y, 25 * pulse, 0, Math.PI * 2);
        ctx.strokeStyle = '#8b5cf6';
        ctx.lineWidth = 3;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y, 15, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(139, 92, 246, ${0.3 * pulse})`;
        ctx.fill();
      } else if (exit.portalType === 'gate') {
        // Gate/door
        ctx.fillStyle = '#4a4a4a';
        ctx.fillRect(screenPos.x - 15, screenPos.y - 30, 30, 40);
        ctx.strokeStyle = '#8b4513';
        ctx.lineWidth = 2;
        ctx.strokeRect(screenPos.x - 15, screenPos.y - 30, 30, 40);
      } else {
        // Path (simple arrow)
        ctx.beginPath();
        ctx.moveTo(screenPos.x, screenPos.y - 15);
        ctx.lineTo(screenPos.x + 10, screenPos.y);
        ctx.lineTo(screenPos.x, screenPos.y + 15);
        ctx.closePath();
        ctx.fillStyle = '#ffd700';
        ctx.fill();
      }

      // Zone name label
      const targetZone = this.zoneManager.getZone(exit.targetZone);
      ctx.font = '12px Arial';
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.fillText(targetZone.name, screenPos.x, screenPos.y + 45);



      // Check if player is near this exit
      const dx = this.player.position.x - exit.position.x;
      const dy = this.player.position.y - exit.position.y;
      const distSqToExit = dx * dx + dy * dy;

      if (distSqToExit < 2500) { // 50*50
        // Show F key hint with pulsing effect
        const pulse = Math.sin(this._frameTime * 5) * 0.2 + 0.8;
        ctx.font = 'bold 14px Arial';
        ctx.fillStyle = `rgba(255, 215, 0, ${pulse})`;
        ctx.fillText(t('transition.interact'), screenPos.x, screenPos.y - 40);
      }

      ctx.restore();
    }
  }

  private renderZoneName(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    ctx.save();

    // Small static zone name at top center
    ctx.font = 'bold 16px Arial';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.shadowColor = '#000000';
    ctx.shadowBlur = 4;
    ctx.fillText(this.currentZoneConfig.name, width / 2, 30);

    const { min, max } = this.currentZoneConfig.levelRange;
    ctx.font = '12px Arial';
    ctx.fillStyle = '#aaaaaa';
    ctx.fillText(t('ui.levelRange', { min, max }), width / 2, 48);

    // Big animated zone name on zone transition
    if (this.zoneNameAnim.active) {
      const elapsed = this.zoneNameAnim.timer;
      const dur = this.zoneNameAnim.duration;
      // Fade in for first 0.5s, hold, fade out last 0.8s
      let alpha = 1;
      if (elapsed < 0.5) {
        alpha = elapsed / 0.5;
      } else if (elapsed > dur - 0.8) {
        alpha = (dur - elapsed) / 0.8;
      }
      alpha = Math.max(0, Math.min(1, alpha));

      // Slight slide-up effect
      const slideOffset = elapsed < 0.5 ? (1 - elapsed / 0.5) * 20 : 0;

      ctx.globalAlpha = alpha;

      // Dark backdrop
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.fillRect(0, height * 0.3 - 30 + slideOffset, width, 80);

      // Zone name (big)
      ctx.font = 'bold 36px Arial';
      ctx.fillStyle = '#ffd700';
      ctx.textAlign = 'center';
      ctx.shadowColor = '#000000';
      ctx.shadowBlur = 8;
      ctx.fillText(this.zoneNameAnim.zoneName, width / 2, height * 0.3 + 10 + slideOffset);

      // Level range subtitle
      ctx.font = '16px Arial';
      ctx.fillStyle = '#cccccc';
      ctx.shadowBlur = 4;
      ctx.fillText(
        t('ui.levelRange', { min: this.zoneNameAnim.levelMin, max: this.zoneNameAnim.levelMax }),
        width / 2,
        height * 0.3 + 35 + slideOffset
      );

      ctx.globalAlpha = 1;
    }

    // Boss phase announcement (centered, fades in/out)
    if (this.phaseAnnounce.active) {
      const elapsed = this.phaseAnnounce.timer;
      const dur = this.phaseAnnounce.duration;
      let alpha = 1;
      if (elapsed < 0.3) {
        alpha = elapsed / 0.3;
      } else if (elapsed > dur - 0.5) {
        alpha = (dur - elapsed) / 0.5;
      }
      alpha = Math.max(0, Math.min(1, alpha));

      const scaleIn = elapsed < 0.3 ? 0.5 + (elapsed / 0.3) * 0.5 : 1;

      ctx.globalAlpha = alpha;

      // Dark backdrop
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.fillRect(0, height * 0.4 - 25, width, 50);

      // Phase text
      ctx.font = `bold ${Math.round(28 * scaleIn)}px Arial`;
      ctx.fillStyle = '#ff44ff';
      ctx.textAlign = 'center';
      ctx.shadowColor = '#000000';
      ctx.shadowBlur = 8;
      ctx.fillText(this.phaseAnnounce.text, width / 2, height * 0.4 + 8);

      ctx.globalAlpha = 1;
    }

    ctx.restore();
  }

  // Check if player is near an exit
  checkZoneTransition(): { canTransition: boolean; exit?: ReturnType<ZoneManager['getExitAt']>; reason?: string } {
    const exit = this.zoneManager.getExitAt(
      this.player.position.x,
      this.player.position.y,
      40
    );

    if (!exit) {
      return { canTransition: false };
    }

    const check = this.zoneManager.canEnterZone(exit.targetZone, this.player.level);
    return {
      canTransition: check.canEnter,
      exit,
      reason: check.reason
    };
  }

  // ========================
  // V2 Skill Entity Methods
  // ========================

  /**
   * Spawn a trap at the target position
   */
  spawnTrap(skill: CustomSkillV2, targetPos: Vector2): void {
    const trap = createTrapEntity({
      skill,
      position: targetPos,
      ownerId: this.player.id,
      baseDamage: this.player.attackDamage,
      getDamage: () => this.player.attackDamage,
    });
    this.traps.push(trap);
    this.particleSystem.emitMagic(targetPos.x, targetPos.y, '#ff8800');
  }

  /**
   * Spawn a totem at the target position
   */
  spawnTotem(skill: CustomSkillV2, targetPos: Vector2): void {
    const totem = createTotemEntity({
      skill,
      position: targetPos,
      ownerId: this.player.id,
      baseDamage: this.player.attackDamage,
      getDamage: () => this.player.attackDamage,
    });
    this.totems.push(totem);
    this.particleSystem.emitMagic(targetPos.x, targetPos.y, '#00ff88');
  }

  /**
   * Start channeling a beam skill
   */
  startBeam(skill: CustomSkillV2, direction: Vector2): void {
    // Check if beam already exists for this skill
    const existingBeam = this.beamEffects.find(b => !b.isComplete);
    if (existingBeam) return;

    const beam = createBeamEffect({
      skill,
      origin: { ...this.player.position },
      direction,
    });
    this.beamEffects.push(beam);
  }

  /**
   * Update active beam direction
   */
  updateBeam(_skill: CustomSkillV2, direction: Vector2, targetDist?: number): void {
    for (const beam of this.beamEffects) {
      if (!beam.isComplete) {
        beam.updatePosition(this.player.position, direction);
        if (targetDist !== undefined) {
          beam.setTargetDistance(targetDist);
        }
      }
    }
  }

  /**
   * Stop all beams for a skill
   */
  stopBeam(_skillId: string, mouseWorldPos?: Vector2): void {
    for (const beam of this.beamEffects) {
      // Overcharge mode: on release, deal burst damage at beam endpoint or mouse position
      if (beam.getBeamMode() === 'overcharge' && !beam.isComplete) {
        const channelTime = beam.getChannelTime();
        const dps = beam.getDPS();
        const burstDamage = Math.floor(dps * channelTime * 2);
        const element = beam.getElement();
        // Use mouse position if within range, otherwise beam endpoint
        let endpoint: Vector2;
        if (mouseWorldPos) {
          const dx = mouseWorldPos.x - beam.origin.x;
          const dy = mouseWorldPos.y - beam.origin.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const range = beam.getRange();
          if (dist <= range) {
            endpoint = mouseWorldPos;
          } else {
            endpoint = beam.getEndpoint();
          }
        } else {
          endpoint = beam.getEndpoint();
        }
        const auraBonus = this.player.getAuraDamageBonus(element);
        const boostedBurst = Math.floor(burstDamage * (1 + auraBonus));

        // AOE burst at beam endpoint
        const burstRadius = 60;
        const hitEnemies = CombatSystem.getEntitiesInRadius(
          this.enemies, endpoint.x, endpoint.y, burstRadius
        );
        for (const enemy of hitEnemies) {
          if (!enemy.isAlive || enemy.isDying) continue;
          const actualDamage = enemy.takeDamage(boostedBurst, element);
          if (actualDamage > 0) {
            getDamageNumbers().spawn(
              enemy.position.x, enemy.position.y - enemy.height,
              actualDamage, element as DamageNumberType
            );
          }
        }
        if (boostedBurst > 0) {
          this.particleSystem.emitExplosion(endpoint.x, endpoint.y, burstRadius);
          this.screenShake.shakeExplosion();
        }
      }
      beam.stop();
    }
    this.player.stopAllChanneling();
  }

  /**
   * Toggle an aura skill on/off
   */
  toggleAura(skill: CustomSkillV2): boolean {
    const toggled = this.player.toggleAura(skill);

    if (toggled && skill.isActive) {
      // Create aura effect
      const aura = createAuraEffect({
        skill,
        position: { ...this.player.position },
      });
      this.auraEffects.push(aura);
    } else if (toggled && !skill.isActive) {
      // Remove the specific aura effect for this skill
      const auraToRemove = this.auraEffects.find(a => a.getSkillId() === skill.id);
      if (auraToRemove) {
        auraToRemove.stop();
      }
      // Swap-and-pop to avoid allocating a new array
      for (let i = this.auraEffects.length - 1; i >= 0; i--) {
        if (this.auraEffects[i].isComplete) {
          this.auraEffects[i] = this.auraEffects[this.auraEffects.length - 1];
          this.auraEffects.pop();
        }
      }
    }

    return toggled;
  }

  /**
   * Apply beam damage to enemies in path
   */
  private applyBeamDamage(beam: BeamEffect, deltaTime: number): void {
    const playerCalc = this.player.getStatCalculator();
    const element = beam.getElement();
    const beamMode = beam.getBeamMode();

    // Overcharge mode: no damage while channeling, damage on release
    if (beamMode === 'overcharge') {
      // Damage is applied in stopBeam() when beam ends
      return;
    }

    // Apply aura bonus to beam damage
    const auraBonus = this.player.getAuraDamageBonus(element);

    // Rampup mode: damage scales with channel time (30% to 150% over 3s)
    let damageMultiplier = 1.0;
    if (beamMode === 'rampup') {
      const channelTime = beam.getChannelTime();
      const rampProgress = Math.min(1, channelTime / 3.0);
      damageMultiplier = 0.3 + rampProgress * 1.2; // 0.3 -> 1.5 over 3s
    }

    for (const enemy of this.enemies) {
      if (!enemy.isAlive || enemy.isDying) continue;

      // Check if enemy is in beam (using hitbox radius for accurate collision)
      const enemyRadius = enemy.getHitboxRadius();
      if (beam.isEntityInBeam(enemy.position.x, enemy.position.y, enemyRadius)) {
        // Calculate DPS damage for this frame (flat spell damage)
        const dps = beam.getDPS();
        let damage = CombatSystem.calculateBeamDamage(
          dps,
          damageMultiplier,
          deltaTime,
          playerCalc
        );

        // Apply aura bonus
        damage = Math.floor(damage * (1 + auraBonus));

        if (damage > 0) {
          const actualDmg = enemy.takeDamage(damage, element);

          // Damage numbers (throttled - show every ~0.3s to avoid spam)
          if (Math.random() < deltaTime * 3) {
            getDamageNumbers().spawn(
              enemy.position.x + (Math.random() - 0.5) * 20,
              enemy.position.y - enemy.height,
              actualDmg,
              element as DamageNumberType
            );
          }

          // Visual feedback (less frequent to avoid spam)
          if (Math.random() < 0.2) {
            this.particleSystem.emitHit(enemy.position.x, enemy.position.y - enemy.height / 2, '#00ffff');
          }
        }
      }
    }
  }

  /**
   * Apply aura effects to nearby entities
   */
  // Aura damage tick timer (damages every 1 second)
  private auraDamageTimer: number = 0;

  private applyAuraEffects(aura: AuraEffect): void {
    // Auras deal periodic DOT damage to enemies in range (1 tick per second)
    // The damage is based on player attack damage * aura effectStrength * 0.2
    // (20% of full damage per tick = decent sustained DPS without being overpowered)
    if (this.auraDamageTimer > 0) return; // Not time for a tick yet

    const element = aura.getElement();
    const strength = aura.getEffectStrength();
    const baseDmg = Math.floor(this.player.attackDamage * strength * 0.2);
    if (baseDmg <= 0) return;

    for (const enemy of this.enemies) {
      if (!enemy.isAlive || enemy.isDying) continue;

      if (aura.isEntityInRange(enemy.position.x, enemy.position.y)) {
        const actualDamage = enemy.takeDamage(baseDmg, element);
        if (actualDamage > 0) {
          getDamageNumbers().spawn(
            enemy.position.x,
            enemy.position.y - enemy.height,
            actualDamage,
            element === 'physical' ? 'physical' : element as DamageNumberType,
          );
        }
      }
    }
  }

  /**
   * Apply trap explosion damage
   */
  private applyTrapExplosion(trap: TrapEntity): void {
    const baseDamage = trap.getDamage();
    const radius = trap.getExplosionRadius();
    const element = trap.getElement();

    // Apply aura bonus to trap damage
    const auraBonus = this.player.getAuraDamageBonus(element);
    const damage = Math.floor(baseDamage * (1 + auraBonus));

    // Find all enemies in explosion radius
    const hitEnemies = CombatSystem.getEntitiesInRadius(
      this.enemies,
      trap.position.x,
      trap.position.y,
      radius
    );

    for (const enemy of hitEnemies) {
      if (!enemy.isAlive || enemy.isDying) continue;

      // Calculate distance-based damage
      const dx = enemy.position.x - trap.position.x;
      const dy = enemy.position.y - trap.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      const actualDamage = CombatSystem.calculateTrapDamage(damage, dist, radius);
      enemy.takeDamage(actualDamage, element);

      // Visual feedback
      getDamageNumbers().spawn(
        enemy.position.x,
        enemy.position.y - enemy.height,
        actualDamage,
        (element as DamageNumberType) || 'physical'
      );
    }

    // Explosion effect
    this.particleSystem.emitExplosion(trap.position.x, trap.position.y, radius);
    this.screenShake.shakeExplosion();
  }

  /**
   * Process totem AI attack
   */
  private processTotemAttack(totem: TotemEntity): void {
    const totemType = totem.getTotemType();

    if (totemType === 'taunt') {
      // Taunt totem: nudge nearby enemies toward the totem position
      for (const enemy of this.enemies) {
        if (!enemy.isAlive || enemy.isDying) continue;
        if (totem.isInRange(enemy.position.x, enemy.position.y)) {
          // Pull enemies toward totem by adjusting their velocity
          const dx = totem.position.x - enemy.position.x;
          const dy = totem.position.y - enemy.position.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 20) {
            const pullSpeed = 30; // px/s nudge
            enemy.position.x += (dx / dist) * pullSpeed * 0.016;
            enemy.position.y += (dy / dist) * pullSpeed * 0.016;
          }
        }
      }
      // Taunt totem doesn't need to call tryAttack for damage
      return;
    }

    if (totemType === 'aoe_pulse') {
      // AOE pulse: deal damage to ALL enemies in range periodically
      // tryAttack handles the cooldown timing, use a dummy target
      const dummyAttack = totem.tryAttack('aoe_pulse', totem.position.x, totem.position.y);
      if (dummyAttack) {
        const auraBonus = this.player.getAuraDamageBonus(dummyAttack.element);
        const boostedDamage = Math.floor(dummyAttack.damage * (1 + auraBonus));

        for (const enemy of this.enemies) {
          if (!enemy.isAlive || enemy.isDying) continue;
          if (totem.isInRange(enemy.position.x, enemy.position.y)) {
            const actualDamage = enemy.takeDamage(boostedDamage, dummyAttack.element);
            getDamageNumbers().spawn(
              enemy.position.x,
              enemy.position.y - enemy.height,
              actualDamage,
              (dummyAttack.element as DamageNumberType) || 'physical'
            );
          }
        }
        // Visual pulse effect
        this.particleSystem.emitExplosion(totem.position.x, totem.position.y, totem.getAttackRange() * 0.5);
      }
      return;
    }

    // Projectile (default) and beam: find nearest enemy and attack
    let nearestEnemy: Enemy | null = null;
    let nearestDist = Infinity;

    for (const enemy of this.enemies) {
      if (!enemy.isAlive || enemy.isDying) continue;

      const dx = enemy.position.x - totem.position.x;
      const dy = enemy.position.y - totem.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < nearestDist && totem.isInRange(enemy.position.x, enemy.position.y)) {
        nearestDist = dist;
        nearestEnemy = enemy;
      }
    }

    if (nearestEnemy) {
      const attack = totem.tryAttack(
        nearestEnemy.id,
        nearestEnemy.position.x,
        nearestEnemy.position.y
      );

      if (attack) {
        // Apply aura bonus to totem damage
        const auraBonus = this.player.getAuraDamageBonus(attack.element);
        const boostedDamage = Math.floor(attack.damage * (1 + auraBonus));

        // Apply damage
        const actualDamage = nearestEnemy.takeDamage(boostedDamage, attack.element);

        // Visual feedback
        this.particleSystem.emitHit(
          nearestEnemy.position.x,
          nearestEnemy.position.y - nearestEnemy.height / 2
        );
        getDamageNumbers().spawn(
          nearestEnemy.position.x,
          nearestEnemy.position.y - nearestEnemy.height,
          actualDamage,
          (attack.element as DamageNumberType) || 'physical'
        );
      }
    }
  }

  /**
   * Handle V2 skill use based on delivery type
   */
  handleSkillV2(skill: CustomSkillV2, targetPos: Vector2): void {
    // Skill cast sound based on delivery type
    const audio = getAudioManager();
    switch (skill.deliveryType) {
      case 'melee': audio.play(Math.random() < 0.5 ? 'slash' : 'slash_1'); break;
      case 'projectile': audio.play('hit_wood'); break;
      case 'aoe': audio.play('hit_metal'); break;
      case 'beam': audio.play('hit_metal_1'); break;
      case 'trap': audio.play('item_drop'); break;
      case 'totem': audio.play('hit_wood_1'); break;
      case 'buff': audio.play('ui_confirm'); break;
      case 'aura': audio.play('ui_confirm'); break;
      case 'curse': audio.play('hit_metal_1'); break;
      case 'minion': audio.play('ui_confirm'); break;
    }

    switch (skill.deliveryType) {
      case 'trap':
        this.spawnTrap(skill, targetPos);
        break;
      case 'totem':
        this.spawnTotem(skill, targetPos);
        break;
      case 'beam': {
        // Beam is handled via channeling in Game.ts
        const dx = targetPos.x - this.player.position.x;
        const dy = targetPos.y - this.player.position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const direction = dist > 0 ? { x: dx / dist, y: dy / dist } : { x: 1, y: 0 };
        this.startBeam(skill, direction);
        break;
      }
      case 'aura':
        this.toggleAura(skill);
        break;
      case 'buff':
        this.applyBuffSkill(skill);
        break;
      case 'curse':
        this.applyCurseSkill(skill, targetPos);
        break;
      case 'minion':
        this.summonMinion(skill, targetPos);
        break;
      default:
        // Other types (projectile, aoe, melee) handled by regular handleSkillAttack
        break;
    }
  }

  /**
   * Apply a buff skill to the player based on element type
   */
  private applyBuffSkill(skill: CustomSkillV2): void {
    const element = SkillCreatorV2.getEffectiveElement(skill);
    const strength = SkillCreatorV2.getEffectStrength(skill);
    const duration = SkillCreatorV2.getBuffDuration(skill);
    const buffType = skill.buffType || 'warcry';

    const sourceId = `skill_buff_${skill.id}`;

    switch (buffType) {
      case 'warcry': {
        // Next X attacks deal +Y% more damage
        const charges = SkillCreatorV2.getBuffCharges(skill);
        // Apply a damage boost buff
        this.player.applyBuff('damage_boost', sourceId, 'skill');
        const activeBuffs = this.player.buffManager.getActiveBuffs();
        for (const buff of activeBuffs) {
          if (buff.sourceId === sourceId) {
            buff.duration = duration;
            buff.remaining = duration;
            buff.value = Math.floor(buff.value * strength);
            // Store charges info as stacks (buff.stacks will decrement on attacks)
            buff.stacks = charges;
            buff.maxStacks = charges;
            break;
          }
        }
        break;
      }
      case 'rage': {
        // +Y% damage but lose Z% HP per second
        const hpCost = SkillCreatorV2.getBuffHpCost(skill);
        this.player.applyBuff('damage_boost', sourceId, 'skill');
        const activeBuffs = this.player.buffManager.getActiveBuffs();
        for (const buff of activeBuffs) {
          if (buff.sourceId === sourceId) {
            buff.duration = duration;
            buff.remaining = duration;
            buff.value = Math.floor(buff.value * strength);
            break;
          }
        }
        // Apply self-DoT: lose HP every 0.5s for duration
        const rageTicks = Math.floor(duration / 0.5);
        let rageTicksLeft = rageTicks;
        const hpPerTick = Math.floor(this.player.stats.maxHp * hpCost / 100 * 0.5);
        const rageInterval = setInterval(() => {
          rageTicksLeft--;
          if (rageTicksLeft <= 0 || !this.player.isAlive) {
            clearInterval(rageInterval);
            return;
          }
          if (hpPerTick > 0 && this.player.stats.hp > hpPerTick) {
            this.player.stats.hp -= hpPerTick;
            getDamageNumbers().spawn(
              this.player.position.x, this.player.position.y - 40,
              hpPerTick, 'physical'
            );
          }
        }, 500);
        break;
      }
      case 'shield': {
        // Create temporary shield that absorbs damage
        const shieldAmount = SkillCreatorV2.getBuffShield(skill);
        const shieldDecay = SkillCreatorV2.getBuffShieldDecay(skill);
        // Apply as an energy shield buff
        this.player.applyBuff('resistance_boost', sourceId, 'skill');
        const activeBuffs = this.player.buffManager.getActiveBuffs();
        for (const buff of activeBuffs) {
          if (buff.sourceId === sourceId) {
            buff.duration = duration;
            buff.remaining = duration;
            buff.value = shieldAmount;
            break;
          }
        }
        // Add temporary ES (energy shield)
        this.player.stats.energyShield = (this.player.stats.energyShield || 0) + shieldAmount;
        // Decay the shield over time
        const decayInterval = setInterval(() => {
          if (!this.player.isAlive || this.player.stats.energyShield <= 0) {
            clearInterval(decayInterval);
            return;
          }
          const decayAmount = Math.floor(shieldDecay * 0.5);
          this.player.stats.energyShield = Math.max(0, this.player.stats.energyShield - decayAmount);
        }, 500);
        // Remove shield after duration
        setTimeout(() => clearInterval(decayInterval), duration * 1000);
        break;
      }
      case 'leech': {
        // For duration, all damage dealt leeches X% as HP
        const leechPercent = SkillCreatorV2.getBuffLeech(skill);
        this.player.applyBuff('damage_boost', sourceId, 'skill');
        const activeBuffs = this.player.buffManager.getActiveBuffs();
        for (const buff of activeBuffs) {
          if (buff.sourceId === sourceId) {
            buff.duration = duration;
            buff.remaining = duration;
            // Store leech info as the buff value (buff system will read this)
            buff.value = leechPercent;
            break;
          }
        }
        break;
      }
      default: {
        // Fallback: element-based buff (legacy behavior)
        let buffTemplateId: string;
        switch (element) {
          case 'od': buffTemplateId = 'damage_boost'; break;
          case 'gok': buffTemplateId = 'attack_speed'; break;
          case 'su': buffTemplateId = 'resistance_boost'; break;
          case 'karanlik': buffTemplateId = 'crit_boost'; break;
          default: buffTemplateId = 'speed_boost'; break;
        }
        this.player.applyBuff(buffTemplateId, sourceId, 'skill');
        const activeBuffs = this.player.buffManager.getActiveBuffs();
        for (const buff of activeBuffs) {
          if (buff.sourceId === sourceId) {
            buff.duration = duration;
            buff.remaining = duration;
            buff.value = Math.floor(buff.value * strength);
            break;
          }
        }
        break;
      }
    }

    // Visual feedback
    const effectColors: Record<string, string> = {
      od: '#ff4400', gok: '#4488ff', su: '#00cccc',
      karanlik: '#9b59b6', physical: '#cccccc',
    };
    this.particleSystem.emitMagic(
      this.player.position.x,
      this.player.position.y,
      effectColors[element] || '#ffffff'
    );
  }

  /**
   * Apply a curse skill - debuff enemies in the target area
   */
  private applyCurseSkill(skill: CustomSkillV2, targetPos: Vector2): void {
    const power = SkillCreatorV2.getCursePower(skill);
    const duration = SkillCreatorV2.getCurseDuration(skill);
    const area = SkillCreatorV2.getCurseArea(skill);
    const element = SkillCreatorV2.getEffectiveElement(skill);
    const curseType = skill.curseType || 'weaken';
    const curseTarget = skill.curseTarget || 'area';

    // Find enemies based on curseTarget
    let hitEnemies: Enemy[];
    let effectivePower = power;

    if (curseTarget === 'single') {
      // Single target: find closest enemy to target position, 1.5x power
      effectivePower = Math.floor(power * 1.5);
      let closest: Enemy | null = null;
      let closestDist = Infinity;
      for (const enemy of this.enemies) {
        if (!enemy.isAlive || enemy.isDying) continue;
        const dx = enemy.position.x - targetPos.x;
        const dy = enemy.position.y - targetPos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < closestDist && dist <= area * 1.5) {
          closestDist = dist;
          closest = enemy;
        }
      }
      hitEnemies = closest ? [closest] : [];
    } else {
      // Area (default): apply to all enemies in radius
      hitEnemies = CombatSystem.getEntitiesInRadius(
        this.enemies,
        targetPos.x,
        targetPos.y,
        area
      );
    }

    for (const enemy of hitEnemies) {
      if (!enemy.isAlive || enemy.isDying) continue;

      // Apply curse based on curseType instead of element
      switch (curseType) {
        case 'weaken':
          // Weaken: enemy deals less damage (use chill as a proxy for damage reduction)
          enemy.applyStatusEffect({
            type: 'chill',
            duration: duration,
            slowPercent: Math.min(60, effectivePower),
          }, this.player.id);
          break;
        case 'fragility':
          // Fragility: enemy takes more damage from all sources
          enemy.applyStatusEffect({
            type: 'shock',
            duration: duration,
            damageAmplify: Math.min(50, effectivePower),
          }, this.player.id);
          break;
        case 'resist_break':
          // Resist break: reduce resistances (use shock + burn for combined debuff)
          enemy.applyStatusEffect({
            type: 'shock',
            duration: duration,
            damageAmplify: Math.min(40, Math.floor(effectivePower * 0.8)),
          }, this.player.id);
          break;
        case 'slow':
          // Slow: reduce movement speed and attack speed
          enemy.applyStatusEffect({
            type: 'chill',
            duration: duration,
            slowPercent: Math.min(80, Math.floor(effectivePower * 1.5)),
          }, this.player.id);
          break;
        default: {
          // Fallback: element-based (legacy behavior)
          let statusType: 'chill' | 'shock' | 'burn' | 'poison' = 'chill';
          switch (element) {
            case 'gok': statusType = 'shock'; break;
            case 'od': statusType = 'burn'; break;
            case 'karanlik': statusType = 'poison'; break;
            default: statusType = 'chill'; break;
          }
          enemy.applyStatusEffect({
            type: statusType,
            duration: duration,
            baseDamage: statusType === 'burn' || statusType === 'poison' ? effectivePower : 0,
            slowPercent: statusType === 'chill' ? Math.min(60, effectivePower) : undefined,
            damageAmplify: statusType === 'shock' ? Math.min(50, effectivePower) : undefined,
          }, this.player.id);
          break;
        }
      }
    }

    // Visual feedback
    const curseColors: Record<string, string> = {
      od: '#ff4400', gok: '#4488ff', su: '#00cccc',
      karanlik: '#9b59b6', physical: '#cccccc',
    };
    this.particleSystem.emitMagic(
      targetPos.x,
      targetPos.y,
      curseColors[element] || '#aa44ff'
    );

    getAudioManager().play('hit_metal_1');
  }

  /**
   * Summon a minion entity. Each minion skill = 1 minion.
   * If a minion for this skill already exists, toggle it off (remove + free mana).
   */
  private summonMinion(skill: CustomSkillV2, targetPos: Vector2): void {
    // Check if a minion for this skill already exists → toggle off
    const existingIndex = this.minions.findIndex(m => m.skillId === skill.id);
    if (existingIndex !== -1) {
      const existing = this.minions[existingIndex];
      // Free reserved mana
      this.player.reservedManaFlat = Math.max(0, this.player.reservedManaFlat - existing.getManaReserve());
      existing.destroy();
      this.minions.splice(existingIndex, 1);
      skill.isActive = false;
      debugLog('Minion', `Dismissed minion for skill ${skill.name}`);
      return;
    }

    // Check if player has enough mana for the reserve
    const manaReserve = SkillCreatorV2.getMinionManaReserve(skill);
    const currentEffectiveMax = this.player.getEffectiveMaxMana();
    if (manaReserve > currentEffectiveMax) {
      getDamageNumbers().spawn(
        this.player.position.x,
        this.player.position.y - 40,
        0,
        'physical',
      );
      return;
    }

    // Create minion
    const minion = createMinionEntity(this.player, skill, targetPos);
    this.minions.push(minion);

    // Reserve mana
    this.player.reservedManaFlat += manaReserve;
    skill.isActive = true;

    // Clamp current mana to new effective max
    const newEffectiveMax = this.player.getEffectiveMaxMana();
    if (this.player.stats.mana > newEffectiveMax) {
      this.player.stats.mana = newEffectiveMax;
    }

    // Visual feedback
    const element = SkillCreatorV2.getEffectiveElement(skill);
    const minionColors: Record<string, string> = {
      od: '#ff4400', gok: '#4488ff', su: '#00cccc',
      karanlik: '#9b59b6', physical: '#cccccc',
    };
    this.particleSystem.emitMagic(
      targetPos.x,
      targetPos.y,
      minionColors[element] || '#ffffff'
    );

    getAudioManager().play('ui_confirm');
    debugLog('Minion', `Summoned ${skill.minionType || 'melee'} minion for skill ${skill.name} (reserve: ${manaReserve} mana)`);
  }

  /**
   * Process minion attack - apply damage to enemies
   */
  private processMinionAttack(minion: MinionEntity): void {
    const attack = minion.getPendingAttack();
    if (!attack) return;

    // Apply aura bonus
    const auraBonus = this.player.getAuraDamageBonus(attack.element);
    const boostedDamage = Math.floor(attack.damage * (1 + auraBonus));

    if (attack.isAOE) {
      // Caster AOE: damage all enemies in radius around target
      for (const enemy of this.enemies) {
        if (!enemy.isAlive || enemy.isDying) continue;
        const dx = enemy.position.x - attack.targetPos.x;
        const dy = enemy.position.y - attack.targetPos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist <= attack.aoeRadius) {
          const actualDamage = enemy.takeDamage(boostedDamage, attack.element);
          getDamageNumbers().spawn(
            enemy.position.x,
            enemy.position.y - enemy.height,
            actualDamage,
            (attack.element as DamageNumberType) || 'physical'
          );
        }
      }
      // AOE visual
      this.particleSystem.emitExplosion(attack.targetPos.x, attack.targetPos.y, attack.aoeRadius * 0.5);
    } else {
      // Single target: find the target enemy
      const target = this.enemies.find(e => e.id === attack.targetId && e.isAlive && !e.isDying);
      if (target) {
        const actualDamage = target.takeDamage(boostedDamage, attack.element);
        this.particleSystem.emitHit(
          target.position.x,
          target.position.y - target.height / 2
        );
        getDamageNumbers().spawn(
          target.position.x,
          target.position.y - target.height,
          actualDamage,
          (attack.element as DamageNumberType) || 'physical'
        );
      }
    }
  }
}
